<template>
  <div class="lessons-view u-reduce-motion">
    <!-- The standing free-sample readout, above every branch for the same
         reason the Voice Ledger is: it describes the account, not the course.
         It stands down only where the lesson wall ACTUALLY renders, which is
         the wall's own render condition and not the flag alone: the wall is
         nested under isPlanInProgress, and the free lesson count pools across
         languages, so a learner who spent their lesson in Spanish and switched
         to French lands on the welcome branch with showLimitWall set and no
         wall on screen. Hiding on the flag there would strip that screen's
         only counts readout and its only upsell affordance.
         Deliberately NOT hidden for showLimitUpsell: that Modal teleports to
         body and overlays the card, so hiding it would shift the layout behind
         the scrim. -->
    <FreeTierUsagePill
      v-if="!(showLimitWall && isPlanInProgress)"
      :user-data="studentInfo"
      :usage="lifetimeUsage"
      :sheet-open="plansSheetOpen"
      source="course"
      @view-plans="openPlansSheet"
    />

    <!-- Voice Ledger: sits above both branches, so the learner sees their
         real speaking time whether or not a plan exists yet. -->
    <VoiceLedgerCard
      v-if="featureFlags.voiceLedger"
      :course-data="courseData"
      :language-code="studentInfo?.selectedLanguage"
      :inflight-lesson-ids="inflightLessonIds"
      @select-lesson="handleLessonClick"
    />

    <!-- The pre-plan body has two shapes. Before a course exists it is the
         marketing welcome hero; once one does it is the course setup screen
         (navy stage panel, one focus card carrying the only available action,
         and a quiet three-row step list). -->
    <div v-if="!isPlanInProgress" class="welcome-container">
      <div v-if="!courseData" class="welcome-header">
        <div class="empty-state-icon">
          <font-awesome-icon icon="graduation-cap" />
        </div>
        <h1>{{ $t('course.welcome.title') }}</h1>
        <p class="welcome-description">
          {{ $t('course.welcome.description') }}
        </p>
        <button
          class="start-journey-btn"
          :disabled="isLoading"
          @click="startJourney"
        >
          {{ isLoading ? $t('common.loading') : $t('course.welcome.startButton') }}
        </button>
      </div>

      <!-- One block child rather than three siblings: .welcome-container is a
           ROW flexbox whose `align-items: flex-start` is the guard against the
           documented top-clipping bug, and adding `flex-direction: column`
           there would silently turn that declaration into a shrink-wrap rule. -->
      <div v-else class="course-setup">
        <StagePanel
          :eyebrow="
            $t('courseSetup.stepOf', {
              current: stagePresentation.stepIndex,
              total: setupStepTotal,
            })
          "
          :headline="$t(stageHeadlineKey(stagePresentation.effectiveStage))"
          :intro="$t(stageIntroKey(stagePresentation.effectiveStage))"
          :segments="stageSegments"
        >
          <div class="focus-header">
            <span class="focus-icon" aria-hidden="true">
              <font-awesome-icon
                :icon="stageIcon(stagePresentation.effectiveStage)"
              />
            </span>
            <div class="focus-text">
              <h2 class="focus-title">
                {{ $t(stageTitleKey(stagePresentation.effectiveStage)) }}
              </h2>
              <p class="focus-description">
                {{ $t(stageDescriptionKey(stagePresentation.effectiveStage)) }}
              </p>
            </div>
          </div>

          <!-- Exactly one action, or none at all. The three predicates are
               mutually exclusive by construction: each requires a different
               effective stage, and the spinner keys on the real pointer. -->
          <div
            v-if="showStageAction"
            class="focus-action"
            data-test="stage-action"
          >
            <button
              v-if="showContinueSetupButton"
              class="button setup-continue"
              data-test="continue-setup"
              :disabled="continueTapPending"
              @click="reopenSetup"
            >
              {{
                continueTapPending
                  ? $t('common.loading')
                  : $t('common.continue')
              }}
            </button>
            <button
              v-else-if="showStartAssessmentButton"
              class="button assessment"
              :disabled="isAssessmentLoading"
              @click="startAssessment"
            >
              {{
                isAssessmentLoading
                  ? $t('course.progress.assessment.starting')
                  : $t('course.progress.assessment.startButton')
              }}
            </button>
            <!-- role="status" as well as aria-live: this element is inserted
                 when the stage advances rather than being updated in place,
                 and role="status" is the form screen readers honour most
                 consistently for a region that arrives with its text already
                 in it. -->
            <p
              v-else-if="showPlanCrafting"
              class="stage-crafting"
              data-test="plan-crafting"
              role="status"
              aria-live="polite"
            >
              <font-awesome-icon
                icon="spinner"
                spin
                class="crafting-spinner"
                aria-hidden="true"
              />
              <span>{{ $t('course.progress.plan.crafting') }}</span>
            </p>
          </div>
        </StagePanel>

        <!-- Named through its own caption: a <section> with no accessible
             name is not announced as a region, so the caption that is already
             on screen does the naming instead of a second, hidden string. -->
        <section class="setup-list" aria-labelledby="course-setup-list-label">
          <p id="course-setup-list-label" class="setup-list-label">
            {{ $t('course.progress.title') }}
          </p>
          <!-- role="list" is not redundant here: Safari and the iOS
               WebViews drop list semantics from the accessibility tree once
               `list-style: none` is applied, which would make VoiceOver read
               three unrelated rows instead of a three-step sequence. -->
          <ol class="setup-steps" role="list">
            <li
              v-for="(row, index) in stagePresentation.rows"
              :key="row.stage"
              class="setup-step"
              :class="row.status"
            >
              <!-- The disc carries the colour; the status word on the right
                   carries the meaning for assistive tech. -->
              <span class="step-disc" data-test="step-disc" aria-hidden="true">
                <font-awesome-icon v-if="row.status === 'done'" icon="check" />
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span class="step-name">{{ $t(stageTitleKey(row.stage)) }}</span>
              <span class="step-status" data-test="step-status">
                {{ $t(stageStatusKey(row.status)) }}
              </span>
            </li>
          </ol>
        </section>
      </div>
    </div>

    <!-- Lessons List Panel with nested Unit → Week → Lessons -->
    <div v-if="isPlanInProgress" class="lessons-list">
      <div class="list-header">
        <h2>{{ $t('course.list.title') }}</h2>
      </div>
      <!-- The wall is a statement about a RESOLVED verdict AND the absence of
           anything to resume. An unresolved verdict, or an open lesson still
           waiting in the tree, renders the tree below instead. -->
      <LessonLimitReached
        v-if="showLimitWall"
        v-bind="limitBlockProps"
        @go-to-chat="goToFreeChat"
        @iap-purchase-complete="handleIapPurchaseComplete"
      />
      <div v-else class="units">
        <div
          v-for="unit in structuredPlan"
          :key="unit.number"
          class="unit"
          :class="{ collapsed: isUnitCollapsed(unit.number) }"
        >
          <!-- Unit Header with Toggle -->
          <div class="unit-header" @click="toggleUnit(unit.number)">
            <div class="unit-header-content">
              <div class="unit-number u-tabular">{{ $t('course.list.unit') }} {{ unit.number }}</div>
              <h3 class="unit-title">{{ unit.title }}</h3>
              <p class="unit-goal">{{ unit.goal }}</p>
            </div>
            <font-awesome-icon
              :icon="
                isUnitCollapsed(unit.number) ? 'chevron-down' : 'chevron-up'
              "
            />
          </div>

          <!-- Unit Content -->
          <div class="weeks" v-show="!isUnitCollapsed(unit.number)">
            <div v-for="week in unit.weeks" :key="week.number" class="week">
              <!-- Week Header with Toggle -->
              <div
                class="week-header"
                @click="toggleWeek(unit.number, week.number)"
              >
                <div class="week-header-content">
                  <div class="week-number u-tabular">{{ $t('course.list.week') }} {{ week.number }}</div>
                  <h4 class="week-title">{{ week.title }}</h4>
                </div>
                <font-awesome-icon
                  :icon="
                    isWeekCollapsed(unit.number, week.number)
                      ? 'chevron-down'
                      : 'chevron-up'
                  "
                />
              </div>

              <!-- Week Content -->
              <div
                class="lessons-grid"
                v-show="!isWeekCollapsed(unit.number, week.number)"
              >
                <div
                  v-for="lesson in week.lessons"
                  :key="lesson.number"
                  class="lesson-card"
                  :class="{
                    active:
                      selectedLesson?.id ===
                      lessonId(unit.number, week.number, lesson.number),
                    completed: isLessonCompleted(lesson),
                    locked: isLessonLocked(
                      unit.number,
                      week.number,
                      lesson.number
                    ),
                    'in-progress': isLessonInProgress(
                      lesson,
                      unit.number,
                      week.number
                    ),
                  }"
                  :aria-disabled="
                    isLessonLocked(unit.number, week.number, lesson.number)
                  "
                  @click="handleLessonClick(lesson, unit.number, week.number)"
                >
                  <div class="lesson-status-icon">
                    <font-awesome-icon
                      v-if="isLessonCompleted(lesson)"
                      icon="check-circle"
                    />
                    <font-awesome-icon
                      v-else-if="
                        isLessonLocked(unit.number, week.number, lesson.number)
                      "
                      icon="lock"
                    />
                    <font-awesome-icon
                      v-else-if="
                        isLessonInProgress(lesson, unit.number, week.number)
                      "
                      icon="spinner"
                      spin
                    />
                  </div>
                  <div class="lesson-number u-tabular">{{ lesson.number }}</div>
                  <h3>{{ lesson.title }}</h3>
                  <!-- Always-visible hint (hover tooltips clip under the
                       card's overflow and never fire on touch). -->
                  <div
                    v-if="
                      isLessonLocked(unit.number, week.number, lesson.number)
                    "
                    class="locked-hint"
                  >
                    {{ $t('course.list.lockedTooltip') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- The at-limit tap affordance: the SAME allowance block, raised over the
         course tree instead of replacing it. Non-destructive on purpose — a
         learner who mis-taps a locked-behind-the-ceiling lesson has to be able
         to dismiss this and go straight back to resuming the one they have
         open. Named from an existing key; no new copy is introduced here. -->
    <Modal
      v-model="showLimitUpsell"
      hide-footer
      :aria-label="limitUpsellLabel"
      :dialog-class="'modal-dialog-centered modal-dialog-scrollable'"
    >
      <LessonLimitReached
        class="limit-reached-sheet"
        v-bind="limitBlockProps"
        @go-to-chat="goToFreeChat"
        @iap-purchase-complete="handleIapPurchaseComplete"
      />
    </Modal>

    <!-- The plans sheet the free-tier usage card raises. A SECOND dialog rather
         than a reuse of the limit sheet above: that one is the at-limit
         allowance block (recap copy, "go to chat" next step) and this one is
         just the plans, raised by a reader who is not blocked on anything. It
         is fed by the same billing state, so the two can never quote different
         prices. Presentation matches: the Modal supplies the surface, so
         UpgradeOptions goes in bare instead of stacking a second card. -->
    <Modal
      v-model="plansSheetOpen"
      hide-footer
      :aria-label="$t('freeTierPill.viewPlans')"
      :dialog-class="'modal-dialog-centered modal-dialog-scrollable'"
    >
      <UpgradeOptions
        :current-plan-id="currentPlanId"
        :user-country="userCountry"
        :current-provider="currentProvider"
        :on-upgrade="handleUpgradeRequest"
        @iap-purchase-complete="handleIapPurchaseComplete"
      />
    </Modal>

    <!-- Course-setup wizard: mounted directly from the COLLECT_BACKGROUND
         stage (no chat dialog involved on this path). -->
    <CourseSetupWizard
      v-if="wizardOpen"
      :dialogId="wizardDialogId"
      :createState="wizardCreateState"
      :createRetryable="wizardCreateRetryable"
      @completed="handleWizardCompleted"
      @close="handleWizardClosed"
      @chat-instead="handleWizardChatInstead"
      @retry-create="retryCourseCreate"
    />

    <!-- Add ChatScreen when dialogId exists -->
    <div v-if="dialogId" class="fullscreen-chat">
      <ChatScreen
        :dialogId="dialogId"
        :metadata="{
          ...courseData?.metadata,
          assessment: courseData?.assessment,
        }"
        @newChatRequest="resetDialog"
        class="chat-screen-container"
      />
    </div>

    <div v-else-if="selectedLesson" class="lesson-chat">
      <!-- Existing lesson chat content... -->
    </div>
  </div>
</template>

<script>
import { ref, shallowRef, computed, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLessonHistoryMarker } from "@/composables/useLessonHistoryMarker.js";
import ChatScreen from "@/components/ChatScreen.vue";
import CourseSetupWizard from "@/components/CourseSetupWizard.vue";
import VoiceLedgerCard from "@/components/VoiceLedgerCard.vue";
import StagePanel from "@/components/StagePanel.vue";
import { normalizePlanUnits, findLatestLesson } from "@/scripts/coursePlan.js";
import {
  SETUP_STAGES,
  resolveCourseStagePresentation,
} from "@/scripts/courseStagePresentation.js";
import { singleFlight } from "@/scripts/singleFlight.js";
import { featureFlags } from "@/store/featureFlags";
import {
  resolveBackgroundPresentation,
  courseSetupDraftKey,
} from "@/scripts/courseSetupRouting.js";
import {
  isRateLimited,
  readRefusal,
  refusalMessageKey,
} from "@/scripts/continueChatErrors.js";
import { cloudFunctions } from "@/store/cloudFunctions";
import { authStore } from "@/store/auth";
import i18n from "@/locales";
import {
  getDatabase,
  ref as dbRef,
  onValue,
  push,
  set,
  get,
} from "firebase/database";
import { useAssessment } from "@/composables/useAssessment";
import { useToast } from "@/composables/useToast.js";
import { useHaptics } from "@/composables/useHaptics.js";
import { useUpsellEvents } from "@/composables/useUpsellEvents.js";
import { LessonApiPath } from "@/constants/lessonApiPaths";
import { CourseStage } from "@/constants/courseStages";
import { pricingStore } from "@/store/pricing.js";
import { stripeUtils } from "@/scripts/stripeUtils.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import {
  fetchUsageAndIncomplete,
  getStartOfMonthInTimezone,
  getEffectiveFreeLimits,
  isFreeTierAccount,
  isServerFreeUser,
  resolveMonthlyLimit,
  SERVER_DEFAULT_TIMEZONE,
} from "@/composables/useUsageCounts.js";
import { Routes } from "@/constants/routes.js";
import Modal from "@/components/Modal.vue";
import LessonLimitReached from "@/components/LessonLimitReached.vue";
import UpgradeOptions from "@/components/UpgradeOptions.vue";
import FreeTierUsagePill from "@/components/FreeTierUsagePill.vue";
import { STORAGE_KEYS } from "@/constants/storageKeys.js";

// Lessons whose dialog-meta write is still in flight, keyed by user, language
// and lesson. Module scope on purpose: an RTDB write offline stays pending
// with the write queued locally, and navigating away from My Course and back
// remounts this view. A guard that died with the old instance would let the
// next tap queue a SECOND dialog behind the first, and both would commit on
// reconnect: two openers generated, two lessons counted, one conversation
// stranded. Keyed rather than a flag so an unrelated lesson stays tappable,
// and cleared in a finally so a rejected write frees it immediately.
const lessonCreatesInFlight = new Set();

// How long a localStorage record of a lesson start stays a resume source.
//
// Five minutes rather than the sixty seconds FreeChatView uses
// (FreeChatView.vue:192), because the two clocks start at different moments.
// The client's starts at the tap, before the RTDB create propagates and before
// the newChat container is invoked; the server's 180-second generation budget
// starts at delivery. A three-minute TTL could therefore expire seconds before
// a delayed invocation finished, and the retap it invites would create a second
// dialog no per-dialog claim can stop. Five minutes leaves two minutes of
// margin over that budget. It is not longer than that because a container
// killed mid-generation leaves dialog-meta in place, which no probe can detect:
// for that case the TTL IS the recovery, and a longer one traps the learner in
// a dead chat for longer.
const LESSON_INFLIGHT_TTL_MS = 300_000;

// The three setup stages are named one way by `CourseStage` and another way by
// the locale bundles (`course.setup.backgroundInfo`, `course.progress.plan`,
// and so on), so this is the single place the two vocabularies meet. Every icon
// named here is already in the registered SVG library (src/main.js), which is
// what the screen renders icons through; the raw `<i class="fas fa-check">`
// tags the old checklist used were the only Font Awesome webfont classes left
// in src/, and no Font Awesome CSS is imported anywhere, so they drew nothing.
const STAGE_PRESENTATION = {
  [CourseStage.COLLECT_BACKGROUND]: { copyKey: "backgroundInfo", icon: "user" },
  [CourseStage.ASSESS_LEARNER]: { copyKey: "assessment", icon: "microphone" },
  [CourseStage.BUILD_PLAN]: { copyKey: "plan", icon: "spinner" },
};

// The setup list's right-hand status word, one per row status the presentation
// helper can return.
const STAGE_STATUS_KEYS = {
  done: "course.setup.statusDone",
  current: "course.setup.statusNow",
  upcoming: "course.setup.statusUpNext",
};

export default {
  name: "LessonsView",
  components: {
    ChatScreen,
    CourseSetupWizard,
    StagePanel,
    Modal,
    LessonLimitReached,
    UpgradeOptions,
    VoiceLedgerCard,
    FreeTierUsagePill,
  },

  setup() {
    const { showToast } = useToast();
    const { impactLight } = useHaptics();
    const upsellEvents = useUpsellEvents();
    const selectedLesson = ref(null);
    const filterStatus = ref("all");
    const dialogId = ref(null);
    // The course-setup wizard is shown instead of a chat. Deliberately a PAIR
    // rather than one overloaded id: the wizard is mounted before the server
    // has minted a dialog id, so "is it on screen" and "which dialog is it
    // bound to" are independent facts.
    const wizardOpen = ref(false);
    const wizardDialogId = ref(null);
    // pending | ready | error — the course-create state machine behind an
    // optimistic mount. Starts `ready` until a create is requested.
    const wizardCreateState = ref("ready");
    // Whether the `error` state can be resolved by re-issuing the request. A
    // 403 languageNotEntitled cannot, and offering Retry there would be a loop.
    const wizardCreateRetryable = ref(true);
    // The retained, LANGUAGE-KEYED create record: { lang, promise }. Retained
    // so closing the wizard mid-flight and retapping Start reuses the same
    // invocation instead of racing a second one (a client abort does not
    // cancel the server call). singleFlight.js is deliberately not used: it
    // drops re-entrant calls and resolves them to undefined.
    // shallowRef, NOT ref: a deep ref hands back a reactive PROXY of the
    // record, so the settle handlers' identity check (`is this still the
    // current record?`) would compare a proxy against the raw object and
    // never match.
    const inFlightCreate = shallowRef(null);
    // The learner closed the wizard this visit; don't auto-reopen it until
    // they tap Continue (or the view remounts).
    const setupDismissed = ref(false);
    const route = useRoute();
    const router = useRouter();
    const { openMarker, stripMarker } = useLessonHistoryMarker({
      dialogId,
      route,
      router,
      onExit: () => resetDialog(),
    });
    const studentInfo = ref(null);
    const courseData = ref(null);
    // Holds the active course `onValue` unsubscribe so repeated
    // watchLatestLesson() calls detach the previous listener instead of
    // stacking duplicate persistent listeners.
    let stopWatchingCourse = null;
    // Holds the user-info `onValue` unsubscribe so it can be torn down on unmount.
    let stopWatchingUser = null;
    // Tracks whether the view is still mounted so an in-flight watchLatestLesson
    // get() that resolves after unmount doesn't register an orphaned listener.
    let isMounted = true;

    // Detach both Realtime Database listeners when the view unmounts so they
    // don't keep firing (and leaking) after navigating away.
    onUnmounted(() => {
      isMounted = false;
      stopWatchingCourse?.();
      stopWatchingUser?.();
    });
    const isLoading = ref(true);
    const lessons = ref(null);
    const hasReachedLimit = ref(false);
    const isProcessingUpgrade = ref(false);
    const isFreePlan = ref(false);
    const currentPlanId = ref("free");
    const userCountry = ref("US");
    // Provider of the active subscription (null on free) so UpgradeOptions only
    // offers the payment path that provider supports.
    const currentProvider = ref(null);

    const handleIapPurchaseComplete = () => {
      hasReachedLimit.value = false;
      // The wall unmounts itself the moment hasReachedLimit clears, but the
      // upsell SHEET has its own flag and would sit over the course the
      // purchase just unlocked until the learner dismissed it by hand. Same
      // for the plans sheet, which is a different dialog with a different flag
      // and can be the one that is open when the purchase lands.
      showLimitUpsell.value = false;
      plansSheetOpen.value = false;
    };

    // The plans sheet raised by the free-tier usage card. Its own flag, like
    // showLimitUpsell: they are two different dialogs, and neither may close
    // or reopen the other.
    const plansSheetOpen = ref(false);

    const openPlansSheet = () => {
      plansSheetOpen.value = true;
      upsellEvents.trackSheetOpen({ source: "course" });
    };

    // Stripe grants arrive by webhook while the sheet is open; the native path
    // closes through iap-purchase-complete above. Fluency is the ONLY plan
    // worth closing on, because it is the only one that leaves UpgradeOptions
    // with an empty body. A mid-session Foundation grant leaves the sheet up
    // showing the Fluency upsell, which is both renderable and correct.
    watch(currentPlanId, (planId) => {
      if (planId === "fluency") {
        plansSheetOpen.value = false;
      }
    });

    // The free lifetime ceiling this account is actually held to: the plan's
    // base value plus any well-formed users/{uid}/freebies grant. Derived from
    // the live studentInfo snapshot and read through the shared helper so it
    // matches helpers/users.checkFreeUserLimit instead of re-deriving the node.
    const effectiveLessonLimit = computed(
      () => getEffectiveFreeLimits(studentInfo.value).lessons
    );

    // The lifetime counts the free-tier verdict below was computed from, kept
    // so FreeTierUsagePill can show the same numbers this view is gating on
    // instead of running a second fan-read over dialogs/{uid}. Null for a paid
    // account, whose allowance is monthly and whose pill is hidden anyway.
    const lifetimeUsage = ref(null);

    // Whether the softer sample-complete landing replaces the legacy wall.
    // Mirrors FreeChatView: `lifetimeUsage` is only populated on the FREE
    // branch of checkPlanLimit, so it doubles as proof that the verdict on
    // screen and the recap counts came from the same lifetime read, and
    // isFreeTierAccount is the helper FreeTierUsagePill reads as well.
    const showSampleComplete = computed(
      () =>
        lifetimeUsage.value != null &&
        isFreeTierAccount(studentInfo.value)
    );

    // The other half of the sample, on the freebies-aware ceiling the gate
    // itself uses.
    const hasFreeChatLeft = computed(
      () =>
        (lifetimeUsage.value?.chats ?? 0) <
        getEffectiveFreeLimits(studentInfo.value).chats
    );

    const goToFreeChat = () => router.push(Routes.DASHBOARD_CHAT);

    // hasReachedLimit alone cannot say "we do not know yet", and
    // false-because-unresolved is indistinguishable from
    // false-because-under-the-ceiling. The wall must never render on a guess
    // and no creation path may fire on one, so the verdict is tri-state: this
    // flag is set only by a resolution that actually landed, and cleared again
    // whenever the language being resolved changes underneath it. Mirrors
    // FreeChatView's limitResolved.
    const limitResolved = ref(false);

    // Dialog ids the server-written dialogs/{uid} index still reports as
    // incomplete LESSON sessions, from the last resolved pass. This is the
    // second source of the resumable-lesson predicate: courseData supplies the
    // tile, this confirms the session actually exists server-side. Resume must
    // qualify only from the index (spec), never from course metadata alone.
    const incompleteLessonIds = ref(new Set());

    // Discards a limit check whose snapshot has been superseded. Two snapshots
    // in quick succession (a language switch, then a grant landing) now start
    // overlapping calls, and `isFreeUser` is captured BEFORE the await: without
    // this, an older invocation that crossed the free/paid boundary mid-flight
    // could overwrite the newer verdict. FreeChatView guards the identical
    // hazard with its resolvingLang check.
    let planLimitToken = 0;

    // The planLimitToken the CURRENTLY PUBLISHED verdict was resolved from.
    // `limitResolved` alone says "a verdict has landed at some point", which is
    // enough for the wall (a previously resolved verdict staying on screen
    // through a refresh is correct, and blanking it would flash the tree) but
    // NOT enough to authorize a write: a refresh that has been started and then
    // FAILED leaves the old permissive verdict standing, and the create gate
    // would read it as permission it no longer has. Comparing the two makes
    // "the newest resolution actually succeeded" expressible.
    let resolvedVerdictToken = -1;

    // Resolves the limit verdict AND the resumable-session answer from ONE
    // dialogs/{uid} read. Two separate reads would leave a drift window in
    // which the wall can be painted over a learner whose lesson is still open —
    // and on native WebViews, where this bug was reported, that window is wide.
    //
    // Returns the payload for THIS invocation, or null when the invocation was
    // superseded; a failed read REJECTS. Every call site here is passive (a
    // snapshot arrived, a dialog closed), so they all go through
    // refreshPlanLimit below, which catches and leaves the verdict UNRESOLVED —
    // which is fail-closed: no wall, and no creation.
    const checkPlanLimit = async () => {
      if (!studentInfo.value?.selectedLanguage) return null;

      const token = ++planLimitToken;
      const resolvingLang = studentInfo.value.selectedLanguage;
      // Free users have a lifetime lesson limit; paid users a monthly one.
      // Both pool across all languages, matching the server's counting.
      // `features` (not the billing-derived plan) is the free test, mirroring
      // helpers/users.isFreeUser — the same split that decides whether the
      // server applies the freebies bonus at all. Shared with FreeChatView's
      // gate, which now branches on the identical predicate: the bare
      // `!studentInfo.value?.features` this replaces was a truthiness test, so
      // it read a corrupted `features: false` or `0` as free where the server
      // reads it as paid.
      const isFreeUser = isServerFreeUser(studentInfo.value);
      // The month window comes from users/{uid}/timezone, which is the boundary
      // helpers/dialog.countDialogsThisMonth applies server-side (UsageMeter
      // already reads it the same way). The device zone was close enough while
      // this verdict only decided what to RENDER; it is not close enough now
      // that it also decides whether a create may fire, because a client whose
      // boundary has rolled over before the server's reads the new month's
      // empty allowance and permits a lesson the server then refuses and
      // deletes. `|| SERVER_DEFAULT_TIMEZONE` mirrors the server's own
      // `userData.get("timezone", "UTC")` (helpers/users.checkPaidUserLimit):
      // an account with no stored zone yet is UTC to the server, so falling
      // back to the DEVICE zone here would reintroduce the same disagreement
      // for exactly the accounts least likely to have one. An unusable id
      // still falls back inside the helper. Counting itself is unchanged:
      // `type` restricts DETECTION only.
      const { usage, incomplete } = await fetchUsageAndIncomplete(
        authStore.userId,
        {
          sinceTimestamp: isFreeUser
            ? 0
            : getStartOfMonthInTimezone(
              studentInfo.value?.timezone || SERVER_DEFAULT_TIMEZONE
            ),
          type: "LESSON",
        }
      );
      if (token !== planLimitToken) return null;
      const lessonsCount = usage.lessons;
      lifetimeUsage.value = isFreeUser ? usage : null;
      // Scoped to the language whose course tree is on screen. courseData is
      // read from courses/{uid}/{lang}, so a row filed under another language
      // cannot belong to a tile here; requiring the match keeps an anomalous
      // row on the client-writable dialogs/{uid} subtree from suppressing the
      // wall for a lesson this view would never open.
      incompleteLessonIds.value = new Set(
        incomplete
          .filter((session) => session.lang === resolvingLang)
          .map((session) => session.dialogId)
      );

      if (isFreeUser) {
        hasReachedLimit.value = lessonsCount >= effectiveLessonLimit.value;
        limitResolved.value = true;
        resolvedVerdictToken = token;
        return { atLimit: hasReachedLimit.value, incomplete };
      }

      // The features node first, then the plan behind the billing lookup key,
      // exactly as helpers/users.checkPaidUserLimit resolves it. Null means
      // neither resolved, and the server does not block in that state, so this
      // must not either: the old chain ended at plans.free.lifetimeLessons,
      // which walled an account holding a features node with no usable
      // lessonsPerMonth at ONE lesson while the backend allowed every create.
      // Shared with FreeChatView so the two gates cannot drift apart on it.
      const planLimit = resolveMonthlyLimit(studentInfo.value, "lessonsPerMonth");

      hasReachedLimit.value = planLimit !== null && lessonsCount >= planLimit;
      limitResolved.value = true;
      resolvedVerdictToken = token;
      return { atLimit: hasReachedLimit.value, incomplete };
    };

    // The latest resolution attempt, retained so a lesson tap that lands during
    // the initial read waits for the real verdict instead of guessing at it —
    // guessing "at limit" would show an upsell to a learner who has allowance
    // left, and guessing the other way would mint a dialog the server refuses.
    let planLimitInFlight = null;

    const refreshPlanLimit = () => {
      planLimitInFlight = checkPlanLimit().catch((error) => {
        // A failed read is an UNKNOWN verdict, not a permissive one:
        // limitResolved stays false, which the wall reads as "do not render"
        // and every creation path reads as "not allowed".
        console.error("Failed to resolve the lesson limit:", error);
        return null;
      });
      return planLimitInFlight;
    };

    // The lesson a resume would reopen, from BOTH sources.
    //
    // The course-tree half is `dialogId && !completedAt`, NOT isLessonInProgress
    // (which tests startedAt and never looks at dialogId): a stuck lesson with a
    // startedAt and no dialogId has no conversation to reopen, and treating it
    // as resumable would suppress the wall in favour of a tree whose only
    // tappable tile raises errors.lesson.missingDialog — no resume AND no
    // upsell, strictly worse than the wall.
    //
    // The index half is the cross-check: the dialogId must still appear as an
    // incomplete row in dialogs/{uid}. Course metadata alone is not enough —
    // a completed or deleted session can leave a dialogId on the tile.
    const resumableLesson = computed(() => {
      if (incompleteLessonIds.value.size === 0) return null;
      for (const unit of structuredPlan.value || []) {
        for (const week of unit.weeks || []) {
          for (const lesson of week.lessons || []) {
            if (
              lesson.dialogId &&
              !lesson.completedAt &&
              incompleteLessonIds.value.has(lesson.dialogId)
            ) {
              return lesson;
            }
          }
        }
      }
      return null;
    });

    // The one render condition for the wall: a RESOLVED at-limit verdict with
    // nothing to go back to. While the verdict is pending the tree/spinner
    // branches below render exactly as they do today — never the wall, so the
    // remount this bug is reported on cannot flash it. courseData being null is
    // covered for free: the wall lives inside v-if="isPlanInProgress", which is
    // false until a plan has landed.
    const showLimitWall = computed(
      () => limitResolved.value && hasReachedLimit.value && !resumableLesson.value
    );

    // The at-limit tap affordance. Its own flag, deliberately NOT the wall's:
    // raising it must not unmount the course tree, because the lesson the
    // learner still has open is in that tree.
    const showLimitUpsell = ref(false);

    // One binding for both presentations of the allowance block, so the sheet
    // and the wall cannot drift.
    const limitBlockProps = computed(() => ({
      showSampleComplete: showSampleComplete.value,
      hasFreeChatLeft: hasFreeChatLeft.value,
      isFreePlan: isFreePlan.value,
      currentPlanId: currentPlanId.value,
      lessonLimit: effectiveLessonLimit.value,
      userCountry: userCountry.value,
      currentProvider: currentProvider.value,
      onUpgrade: handleUpgradeRequest,
    }));

    // The dialog's accessible name, taken from whichever heading the block is
    // about to render. Existing keys only — a bespoke string here would trip
    // the literal-copy gate and the 31-locale parity manifest.
    const limitUpsellLabel = computed(() =>
      i18n.global.t(
        showSampleComplete.value
          ? "course.sampleComplete.title"
          : "course.limits.title"
      )
    );

    // The fail-closed creation gate, as a tri-state rather than a boolean.
    //
    // "unknown" is NOT folded into "atLimit": both refuse the write, but only
    // one of them is a true statement about the learner's allowance, and
    // telling someone their sample is spent because a read failed is its own
    // bug. An unresolved-but-in-flight verdict is awaited rather than guessed:
    // guessing "atLimit" would upsell a learner who has allowance left, and
    // guessing the other way would mint a dialog the server refuses and
    // deletes.
    //
    // @returns {Promise<"allowed"|"atLimit"|"unknown">}
    const resolveCreationVerdict = async () => {
      // ALWAYS settle the newest resolution, not just when nothing has ever
      // landed. A verdict that is being refreshed is not a current verdict:
      // resetDialog() starts a refresh the moment a lesson is finished, and the
      // pre-refresh "you have allowance left" would otherwise still be standing
      // when the learner taps the next tile — precisely when the lesson they
      // just finished may have spent the last of it. Already-settled is a
      // microtask, so the common path costs nothing.
      // refreshPlanLimit swallows read failures, so this never rejects.
      await planLimitInFlight;
      // Resolved-at-some-point is not enough; it has to be resolved from the
      // newest attempt. A refresh that failed leaves the stale verdict live.
      if (!limitResolved.value || resolvedVerdictToken !== planLimitToken) {
        return "unknown";
      }
      return hasReachedLimit.value ? "atLimit" : "allowed";
    };

    // New computed property for structured plan: Units -> Weeks -> Lessons
    // Normalized once here so the template and every computed below can
    // iterate plain arrays: RTDB hands back an index-keyed object for a
    // sparse plan, which `for...of` throws on.
    const structuredPlan = computed(() => normalizePlanUnits(courseData.value));

    // UPDATED: Use planLessons if available, fall back to mock lessons
    const filteredLessons = computed(() => {
      const sourceLessons = structuredPlan.value || lessons.value;
      if (filterStatus.value === "all") return sourceLessons;
      return sourceLessons.filter(
        (lesson) => lesson.status === filterStatus.value
      );
    });

    const selectLesson = (lesson) => {
      selectedLesson.value = lesson;
    };

    const completeLesson = () => {
      if (selectedLesson.value) {
        selectedLesson.value.status = "completed";
      }
    };

    // Bumped by every watchLatestLesson() call so an earlier, slower read
    // cannot apply its snapshot over a newer one's. RTDB get()s can resolve
    // out of order, and the optimistic mount deliberately fires a read while
    // no course exists yet: without this, that pre-create empty snapshot could
    // land after the create succeeded and reset the view (closing a wizard
    // that had just been handed its dialog id).
    let courseReadGeneration = 0;

    const watchLatestLesson = () => {
      if (!studentInfo.value?.selectedLanguage) return;

      const generation = ++courseReadGeneration;
      const db = getDatabase();
      // First get the latest course to get the courseId
      get(
        dbRef(
          db,
          `courses/${authStore.getUserId()}/${
            studentInfo.value.selectedLanguage
          }`
        )
      )
        .then((snapshot) => {
          // The view may have unmounted while this get() was in flight; don't
          // register a listener that would then never be cleaned up.
          if (!isMounted) return;
          // A later watchLatestLesson() superseded this read; its answer is
          // the current one. Acting on this older snapshot would apply
          // pre-create state on top of post-create state.
          if (generation !== courseReadGeneration) return;
          // Guard against out-of-order resolution: if the selected language
          // changed while this get() was in flight, ignore the stale snapshot
          // so we don't watch a course on a mismatched language path.
          if (snapshot.ref.key !== studentInfo.value?.selectedLanguage) {
            return;
          }
          const courses = snapshot.val();
          if (!courses) {
            stopWatchingCourse?.();
            stopWatchingCourse = null;
            resetCourseState();
            return;
          }

          // Find the active course
          let activeCourseId = null;
          for (const [courseId, course] of Object.entries(courses)) {
            if (course.metadata.currentStage !== CourseStage.COMPLETED) {
              activeCourseId = courseId;
              break;
            }
          }

          if (!activeCourseId) {
            stopWatchingCourse?.();
            stopWatchingCourse = null;
            resetCourseState();
            return;
          }

          // Now watch this specific course for updates
          const courseRef = dbRef(
            db,
            `courses/${authStore.getUserId()}/${
              studentInfo.value.selectedLanguage
            }/${activeCourseId}`
          );

          // Detach any previous course listener before re-subscribing so
          // repeated calls (e.g. closing a lesson) don't stack listeners.
          stopWatchingCourse?.();
          stopWatchingCourse = onValue(
            courseRef,
            (courseSnapshot) => {
              const data = courseSnapshot.val();
              if (!data) {
                resetCourseState();
                return;
              }

              // Ensure courseId is included in metadata
              data.metadata.courseId = activeCourseId;
              courseData.value = data;

              // A stamped dialogId means the server finished the start this
              // record was standing in for, so the record has nothing left to
              // say and the tile can go back to reading the lesson node.
              clearInflightForStampedLessons(
                data,
                studentInfo.value?.selectedLanguage,
                activeCourseId
              );

              // Collapse all units and weeks except for the active ones
              if (normalizePlanUnits(data).length > 0) {
                // Use setTimeout to ensure this runs after the next DOM update
                setTimeout(() => collapseAllExceptActive(), 0);
              }

              // Only open something if nothing is already active — this
              // prevents closing the dialog/wizard when a stage completes.
              if (!dialogId.value && !wizardOpen.value) {
                const currentStage = data.metadata.currentStage;
                // Optional chaining on `stages` itself, not only on the entry
                // under it: a course the backend has created but not yet
                // written a stages map for is a real snapshot, and the bare
                // dereference threw inside this callback.
                if (
                  currentStage &&
                  data.metadata.stages?.[currentStage]?.dialogId &&
                  !data.metadata.stages?.[currentStage]?.completedAt
                ) {
                  if (currentStage === CourseStage.COLLECT_BACKGROUND) {
                    // Setup is a wizard, not a chat — unless the learner
                    // already opted into the conversational interview.
                    routeBackgroundStage(
                      data.metadata.stages[currentStage].dialogId
                    );
                  } else {
                    dialogId.value = data.metadata.stages[currentStage].dialogId;
                  }
                }
              }
            },
            (error) => {
              console.error("Course watch failed:", error);
            }
          );
        })
        .catch((error) => {
          console.error("Failed to get initial course data:", error);
        });
    };

    const resetCourseState = () => {
      courseData.value = null;
      dialogId.value = null;
      // Never tear down a wizard that is still waiting on (or retrying) its
      // own create: while that create is in flight "this language has no
      // course" is the EXPECTED reading, not a signal to close. A course read
      // that started before the create — the one `handleWizardClosed` fires,
      // for instance — used to land here and shut the optimistic wizard, and
      // the success path then read `wizardOpen === false` as a deliberate
      // close and refused to feed the id in.
      //
      // Reset the wizard only after its create has settled and no retained
      // request remains.
      if (wizardCreateState.value === "ready" && !inFlightCreate.value) {
        wizardOpen.value = false;
        wizardDialogId.value = null;
      }
      isLoading.value = false;
    };

    // Decide how to present a pending COLLECT_BACKGROUND stage: the setup
    // wizard by default, or the chat if the learner previously chose the
    // conversational interview (dialog-meta.skippedStructuredInput). The
    // decision is made from persisted state BEFORE anything renders, so
    // returning users never see a wizard flash open and close.
    // Reactive so the setup screen's Continue button stays hidden while the
    // decision is in flight (otherwise it flashes for one network round trip).
    const backgroundRouteInFlight = ref(false);
    // True only while the route was started by the learner tapping Continue.
    // The auto-open path leaves it false, which is what keeps the two paths'
    // feedback apart: a tap gets a visible pending button and an error toast,
    // an auto-open gets neither.
    // Deliberately NOT bounded by a timeout: a read that never settles already
    // pinned backgroundRouteInFlight true before this flag existed (the button
    // was hidden outright, which is strictly worse). Clearing pending on a timer
    // while the route stayed in flight would re-enable a button whose taps the
    // re-entry guard swallows — a dead control wearing an enabled costume.
    const continueTapPending = ref(false);
    const routeBackgroundStage = async (stageDialogId) => {
      if (backgroundRouteInFlight.value || setupDismissed.value) return;
      backgroundRouteInFlight.value = true;
      const langAtStart = studentInfo.value?.selectedLanguage;
      try {
        const snapshot = await get(
          dbRef(getDatabase(), `dialog-meta/${stageDialogId}`)
        );
        if (!isMounted || dialogId.value || wizardOpen.value) return;
        // The user may have switched languages while the get() was in flight;
        // don't open language A's setup over language B's course view.
        if (studentInfo.value?.selectedLanguage !== langAtStart) return;
        const presentation = resolveBackgroundPresentation(snapshot.val());
        if (presentation === "none") {
          if (!snapshot.exists()) {
            console.warn(
              "COLLECT_BACKGROUND stage points at missing dialog-meta:",
              stageDialogId
            );
          }
          // Nothing to open: the stage points at a deleted or already-completed
          // dialog. On the auto-open path that is silent by design, but a
          // learner who tapped Continue would otherwise watch the button come
          // back as a permanent no-op.
          if (continueTapPending.value) {
            showToast(i18n.global.t("courseSetup.skipFailed"), "error");
          }
        }
        if (presentation === "chat") {
          dialogId.value = stageDialogId;
        } else if (presentation === "wizard") {
          wizardDialogId.value = stageDialogId;
          wizardOpen.value = true;
        }
      } catch (error) {
        // Surface the failure (e.g. offline) — otherwise tapping Continue
        // appears to do nothing. Skip the toast if we unmounted mid-request so
        // it can't surface on whatever page the user navigated to.
        console.error("Failed to route background stage:", error);
        if (isMounted) {
          showToast(i18n.global.t("courseSetup.skipFailed"), "error");
        }
      } finally {
        if (isMounted) {
          backgroundRouteInFlight.value = false;
        }
      }
    };

    // Everything the setup screen draws reads from here, so the panel caption,
    // the focus card and the action row can never describe three different
    // stages. `effectiveStage` is the first stage that is not finished, which
    // is not always the stage `currentStage` names: the backend stamps
    // `COLLECT_BACKGROUND.completedAt` in one write and advances the pointer in
    // another, and between those two writes the pointer is a stage behind.
    const stagePresentation = computed(() =>
      resolveCourseStagePresentation(
        courseData.value?.metadata?.currentStage,
        courseData.value?.metadata?.stages
      )
    );

    // The presentation rows, in the shape StagePanel's track takes. Mapped
    // rather than passed straight through, so the shared component depends on
    // a two-field contract instead of on the whole course-stage row.
    const stageSegments = computed(() =>
      stagePresentation.value.rows.map((row) => ({
        key: row.stage,
        status: row.status,
      }))
    );

    const stageCopyKey = (stage) => STAGE_PRESENTATION[stage]?.copyKey;
    const stageHeadlineKey = (stage) => `course.setup.${stageCopyKey(stage)}.headline`;
    const stageIntroKey = (stage) => `course.setup.${stageCopyKey(stage)}.intro`;
    const stageTitleKey = (stage) => `course.progress.${stageCopyKey(stage)}.title`;
    const stageDescriptionKey = (stage) =>
      `course.progress.${stageCopyKey(stage)}.description`;
    const stageIcon = (stage) => STAGE_PRESENTATION[stage]?.icon;
    const stageStatusKey = (status) => STAGE_STATUS_KEYS[status];

    const showContinueSetupButton = computed(() => {
      return (
        // The card and the button must agree about which stage this is. In
        // practice the pointer check below already implies this one, but the
        // two predicates are kept in step deliberately: all three action-row
        // conditions require the matching effective stage.
        stagePresentation.value.effectiveStage ===
          CourseStage.COLLECT_BACKGROUND &&
        courseData.value?.metadata?.currentStage ===
          CourseStage.COLLECT_BACKGROUND &&
        !!courseData.value?.metadata?.stages?.COLLECT_BACKGROUND?.dialogId &&
        !courseData.value?.metadata?.stages?.COLLECT_BACKGROUND?.completedAt &&
        !wizardOpen.value &&
        !dialogId.value &&
        // A tap overrides ONLY the in-flight hide (that hide exists for the
        // auto-open path, where a "Loading..." button would flash at a user who
        // tapped nothing). The wizard and chat vetoes above keep their say, so
        // the button still vanishes the instant either surface takes over.
        (continueTapPending.value || !backgroundRouteInFlight.value)
      );
    });

    const reopenSetup = async () => {
      // `disabled` only lands on the next render, so two taps in the same task
      // both get here. routeBackgroundStage's own re-entry guard stops the
      // second from routing, but it returns immediately — and without this
      // guard the second call's `finally` would clear the pending flag while
      // the first route is still in flight, making the button vanish again.
      if (continueTapPending.value) return;
      impactLight();
      setupDismissed.value = false;
      continueTapPending.value = true;
      try {
        await routeBackgroundStage(
          courseData.value.metadata.stages.COLLECT_BACKGROUND.dialogId
        );
      } finally {
        continueTapPending.value = false;
      }
    };

    const handleWizardCompleted = () => {
      wizardOpen.value = false;
      wizardDialogId.value = null;
      watchLatestLesson();
      refreshPlanLimit();
    };

    const handleWizardClosed = () => {
      setupDismissed.value = true;
      wizardOpen.value = false;
      wizardDialogId.value = null;
      // A fresh journey has no course watcher yet (the course didn't exist on
      // mount); attach one so the setup screen appears behind the wizard.
      watchLatestLesson();
    };

    // Synchronous re-entry guard for the chat-instead switch. The wizard
    // already disables its button until createState is `ready`, so this only
    // has to stop a double tap AFTER that — but it must live in a
    // try/finally, or a rejected switch would leave the action inert forever.
    const chatInsteadBusy = ref(false);

    const handleWizardChatInstead = async () => {
      if (chatInsteadBusy.value) return;
      const id = wizardDialogId.value;
      // Defence in depth: the wizard's disabled binding is what guarantees
      // this, but a switch with no id would POST `dialogId: null`.
      if (!id) return;
      chatInsteadBusy.value = true;
      // Swap to the chat immediately: ChatScreen shows its "Arti is thinking"
      // loader for a zero-message dialog while the backend generates the
      // opening interview question, and autoplays it on arrival.
      wizardOpen.value = false;
      wizardDialogId.value = null;
      dialogId.value = id;
      try {
        const token = await authStore.getIdToken();
        const response = await fetch(cloudFunctions.getContinueChatApi(), {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ dialogId: id, action: "skipStructuredInput" }),
        });
        if (!response.ok) {
          // skipStructuredInput is a bounded continueChat turn like any other,
          // so a learner who has been tapping quickly can be refused here. Read
          // the body before throwing so the catch can say which refusal it was.
          const { code } = await readRefusal(response);
          const error = new Error(`HTTP ${response.status}`);
          error.refusalCode = code;
          throw error;
        }
        // The learner is committed to the conversational path; drop the
        // wizard draft now (kept until here so a failed switch can restore it).
        try {
          localStorage.removeItem(courseSetupDraftKey(id));
        } catch (e) {
          // Storage unavailable: stale draft is harmless.
        }
      } catch (error) {
        console.error("Failed to start conversational setup:", error);
        // The server may have committed the switch even though our request
        // failed client-side (e.g. timeout after the write). Re-check before
        // reverting: reopening the wizard over a committed switch would leave
        // an orphaned interview message and route to chat on the next visit.
        let committed = false;
        try {
          const snap = await get(dbRef(getDatabase(), `dialog-meta/${id}`));
          committed = !!snap.val()?.skippedStructuredInput;
        } catch (e) {
          // Offline: assume not committed and revert to the wizard.
        }
        if (!isMounted || dialogId.value !== id) return;
        if (committed) return; // keep the chat; the opening message will land
        // Only the rate limit gets its own wording. The turn ceiling's copy
        // tells the learner to start a new conversation, and there is no way to
        // start a second course-background dialog from this screen, so for that
        // refusal the generic retry message is the less wrong of the two.
        // Both the code test and the key come from continueChatErrors, which is
        // what keeps this call site inside the ConstantsSyncTest pin.
        const refusalKey = isRateLimited(error?.refusalCode)
          ? refusalMessageKey(error.refusalCode)
          : null;
        showToast(
          i18n.global.t(refusalKey || "courseSetup.skipFailed"),
          "error"
        );
        dialogId.value = null;
        // BOTH refs: reverting only the id would leave neither the chat nor
        // the wizard on screen.
        wizardDialogId.value = id;
        wizardOpen.value = true;
      } finally {
        chatInsteadBusy.value = false;
      }
    };

    // Everything checkPlanLimit's verdict depends on, collapsed to a comparable
    // string. checkPlanLimit does a full dialogs/{uid} fan-read, so re-running
    // it on EVERY users/{uid} snapshot (this node also carries selectedLanguage,
    // notification prefs, tutor voice...) would turn unrelated profile writes
    // into repeated subtree reads. Re-running only when one of these moved is
    // what clears the paywall in place for a freebies grant or an activated
    // subscription without that cost.
    const limitInputsSignature = (data) => {
      const freeLimits = getEffectiveFreeLimits(data);
      // `features` presence is the free/paid split; its monthly value is what
      // the paid branch normally reads. `|| null`, NOT `?? null`, and this is
      // now deliberately CONSERVATIVE rather than exact: resolveMonthlyLimit
      // treats a stored 0 as a real ceiling (the server does), so billing
      // genuinely cannot move such an account's verdict, but keeping billing in
      // the signature for it costs one redundant dialogs/{uid} read in a state
      // no writer can produce, while `?? null` would be a silent no-re-check
      // the moment that reasoning turned out to be wrong.
      // UserFeatures.__post_init__ explicitly permits 0, so it is representable.
      const monthlyLimit = data?.features?.lessonsPerMonth || null;
      // The billing row only reaches the verdict through the plan fallback that
      // runs when features carries no monthly limit. Including it
      // unconditionally would fan out over dialogs/{uid} on an active ->
      // past_due status change that cannot move a feature-limited paywall.
      const usesPlanFallback = monthlyLimit === null;
      // The timezone only reaches the verdict through the PAID branch's month
      // boundary; the free branch counts a lifetime window that no zone can
      // move. It has to be here at all because DashboardView writes
      // users/{uid}/timezone asynchronously on the first dashboard visit, so
      // "absent, then present" is the normal path for a new account rather
      // than an edge case — and the two windows it selects (the server's UTC
      // default, then the device zone it just saved) disagree around both
      // month boundaries. Omitting it left the paid verdict pinned to the UTC
      // window while checkPaidUserLimit had already moved to the stored one.
      // Conditional for the same reason billing is: a free account must not
      // buy a dialogs/{uid} fan-read for a write that cannot move its ceiling.
      // Both `features` entries below ask isServerFreeUser rather than testing
      // the node for truthiness, because checkPlanLimit picks its branch that
      // way: a signature that called a `features: false` account free would
      // drop the timezone, and the paid verdict would then stay pinned to the
      // UTC window after DashboardView wrote the stored one.
      const serverFree = isServerFreeUser(data);
      return JSON.stringify([
        freeLimits.lessons,
        serverFree ? 0 : 1,
        monthlyLimit,
        usesPlanFallback ? data?.billing?.lookupKey ?? null : null,
        usesPlanFallback ? data?.billing?.status ?? null : null,
        serverFree ? null : data?.timezone ?? null,
      ]);
    };
    let lastLimitInputs = null;

    // Update watchStudentInfo to call watchLatestLesson when language changes
    const watchStudentInfo = () => {
      const db = getDatabase();
      stopWatchingUser?.();
      lastLimitInputs = null;
      stopWatchingUser = onValue(
        dbRef(db, "users/" + authStore.getUserId()),
        (snapshot) => {
          const data = snapshot.val();
          const oldLang = studentInfo.value?.selectedLanguage;
          studentInfo.value = data;

          // Determine current plan ID (respects billing.status, so a canceled
          // subscription correctly resolves to free)
          const plan = pricingStore.getCurrentPlan(data?.billing);
          currentPlanId.value = plan.id;
          // Update isFreePlan based on the determined plan ID
          isFreePlan.value = currentPlanId.value === "free";
          // null when free; fall back to "stripe" for a paid plan whose legacy
          // billing row predates the provider field (matches AccountView).
          currentProvider.value =
            plan.id === "free" ? null : data?.billing?.provider || "stripe";
          userCountry.value = platformBilling.getUserCountry(data);

          const limitInputs = limitInputsSignature(data);
          const limitInputsChanged = limitInputs !== lastLimitInputs;
          lastLimitInputs = limitInputs;

          // If language changed or first load, start watching latest lesson
          if (data?.selectedLanguage && data.selectedLanguage !== oldLang) {
            // A wizard dismissal is per-language: switching languages must
            // not suppress auto-opening the other language's setup.
            setupDismissed.value = false;
            // Drop language A's wizard SYNCHRONOUSLY. resetCourseState is not
            // enough: watchLatestLesson only calls it when the destination
            // language has no active course, so switching to a language that
            // does have one would leave an id-less wizard mounted forever
            // while the continuation's language guard (correctly) refuses to
            // feed it. Dropping the retained create matters too, or a later
            // Start tap would "reopen" against the previous language's work.
            wizardOpen.value = false;
            wizardDialogId.value = null;
            wizardCreateState.value = "ready";
            wizardCreateRetryable.value = true;
            inFlightCreate.value = null;
            // The verdict on screen was resolved against language A. Holding it
            // over language B would let the wall paint over a learner whose
            // lesson in B is still open (and would let a create fire on A's
            // allowance). Both facts are re-derived by the pass below.
            limitResolved.value = false;
            incompleteLessonIds.value = new Set();
            watchLatestLesson();
            refreshPlanLimit();
          } else if (limitInputsChanged) {
            // A freebies grant (or a subscription activating) landed while the
            // user was sitting on the paywall. Without this the raised ceiling
            // only took effect on reload, so the explainer modal would announce
            // a bonus behind a paywall that had not moved.
            refreshPlanLimit();
          }
        },
        (error) => {
          console.error("read failed:", error);
        }
      );
    };

    const courseCreateBody = (lang) =>
      JSON.stringify({
        path: LessonApiPath.INFO_GETTING,
        lang,
        // Marks this dialog as wizard-presented so the backend skips the
        // legacy greeting message old clients still need. It also selects the
        // adoptive, concurrency-safe create path (the no-flag branch is the
        // untouched legacy one).
        wizardSetup: true,
      });

    // Bounds the optimistic create. The abort does NOT cancel the server
    // invocation — that is exactly why the endpoint's claim contract exists —
    // it just stops the wizard sitting in `pending` forever behind a hung
    // socket.
    const COURSE_CREATE_TIMEOUT_MS = 15000;

    // Issues the wizard-path create and validates the response FAIL-CLOSED.
    // This repo has a documented placeholder-URL hazard: a relative fetch
    // falls through to the SPA catch-all and 200s with index HTML, so neither
    // `response.ok` nor "the body parsed" is proof a course exists. Only a
    // truthy dialogId is — except on `alreadyActive`, which deliberately
    // carries none.
    const requestCourseCreate = async (lang) => {
      const token = await authStore.getIdToken();
      const controller = new AbortController();
      const timer = setTimeout(
        () => controller.abort(),
        COURSE_CREATE_TIMEOUT_MS
      );
      try {
        const response = await fetch(cloudFunctions.getLessonsApi(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: courseCreateBody(lang),
          signal: controller.signal,
        });
        if (!response.ok) {
          // The endpoint distinguishes 503 courseSetupBusy (another
          // invocation holds the claim; retry converges) from 403
          // languageNotEntitled (retry fails identically forever). Anything
          // unrecognised is treated as retryable, which is the safe default
          // for a transient network error.
          let retryable = true;
          try {
            const body = await response.json();
            if (body?.retryable === false) retryable = false;
          } catch (e) {
            // Non-JSON error body: keep the retryable default.
          }
          const error = new Error(`HTTP ${response.status}`);
          error.retryable = retryable;
          throw error;
        }
        const data = await response.json();
        if (!data || typeof data !== "object") {
          throw new Error("Course create returned a non-object body");
        }
        if (!data.alreadyActive && !data.dialogId) {
          throw new Error("Course create returned no dialogId");
        }
        return data;
      } finally {
        clearTimeout(timer);
      }
    };

    // Returns the retained record for `lang`, starting one if needed. Reuse is
    // language-scoped: a record for the previous language must never satisfy a
    // tap made after switching.
    const ensureCourseCreate = (lang) => {
      const existing = inFlightCreate.value;
      if (existing && existing.lang === lang) return existing;
      const record = { lang };
      record.promise = requestCourseCreate(lang);
      inFlightCreate.value = record;
      record.promise.then(
        (data) => settleCourseCreate(record, data, null),
        (error) => settleCourseCreate(record, null, error)
      );
      return record;
    };

    const settleCourseCreate = (record, data, error) => {
      // Identity check: a stale record (dropped by a language switch) must
      // neither feed the current view nor clear the current request.
      if (inFlightCreate.value !== record) return;
      inFlightCreate.value = null;
      if (!isMounted) return;
      if (studentInfo.value?.selectedLanguage !== record.lang) return;
      if (error) {
        applyCourseCreateFailure(error);
        return;
      }
      applyCourseCreateSuccess(data);
    };

    const applyCourseCreateSuccess = (data) => {
      wizardCreateState.value = "ready";
      wizardCreateRetryable.value = true;
      // Resolve what the wizard should show. `alreadyActive` carries no
      // dialogId; an adopted dialog's meta decides between wizard and chat.
      const outcome = data.alreadyActive
        ? "none"
        : data.created
          ? "wizard"
          : resolveBackgroundPresentation(data.dialogMeta ?? null);

      if (!wizardOpen.value) {
        // Closed while the create was in flight: never reopen over whatever
        // the learner is looking at now. The chat is the one exception —
        // an adopted interview dialog still has to take over the surface.
        if (outcome === "chat") dialogId.value = data.dialogId;
      } else if (outcome === "wizard") {
        wizardDialogId.value = data.dialogId;
      } else if (outcome === "chat") {
        wizardOpen.value = false;
        wizardDialogId.value = null;
        dialogId.value = data.dialogId;
      } else {
        // "none" — the background stage is already finished or unusable.
        // Treated exactly like alreadyActive and never as an error: an error
        // state would offer a Retry that loops against the same adoption,
        // while the setup screen always recovers.
        wizardOpen.value = false;
        wizardDialogId.value = null;
      }

      // Every success path, including the closed-wizard one: the close ran
      // watchLatestLesson against a course that did not exist yet, where it
      // detaches without registering a listener, so the setup screen would never
      // appear without this explicit re-run.
      watchLatestLesson();
    };

    const applyCourseCreateFailure = (error) => {
      console.error("Failed to start journey:", error);
      wizardCreateRetryable.value = error?.retryable !== false;
      if (wizardOpen.value) {
        wizardCreateState.value = "error";
        return;
      }
      // No banner exists to show: the wizard is closed and the Start button is
      // back. Say so and leave it enabled.
      wizardCreateState.value = "ready";
      showToast(i18n.global.t("courseSetup.skipFailed"), "error");
    };

    const retryCourseCreate = () => {
      const lang = studentInfo.value?.selectedLanguage;
      if (!lang) return;
      wizardCreateState.value = "pending";
      wizardCreateRetryable.value = true;
      ensureCourseCreate(lang);
    };

    const startJourney = () => {
      const lang = studentInfo.value?.selectedLanguage;
      if (!lang) {
        console.error("No language selected");
        return;
      }

      // The form needs nothing from the server to render, so mount it
      // now and resolve the id underneath. isLoading is deliberately NOT set —
      // createState supersedes it, and after a close-while-pending the Start
      // button must be tappable again (a retap reuses the retained create).
      wizardDialogId.value = null;
      wizardCreateState.value = "pending";
      wizardCreateRetryable.value = true;
      wizardOpen.value = true;
      ensureCourseCreate(lang);
    };

    // ---- The in-flight lesson-start record ---------------------------------
    //
    // Starting a lesson writes dialog-meta and then waits 10-30 seconds for the
    // backend to generate the opener. Nothing on the tile changes in that
    // window, because the lesson node gets its dialogId only when the server
    // stamps it. A learner who switches tabs, backs out, or is interrupted and
    // taps the tile again therefore used to mint a SECOND dialog: two openers
    // generated, two lessons counted against the plan, one conversation
    // stranded.
    //
    // The cure is the device-local record FreeChatView already keeps for
    // freestyle chats (FreeChatView.vue:185-192). It differs here in being a
    // MAP keyed by user, language, course and lesson coordinates: a learner who
    // starts a lesson in a second tab for another language would otherwise
    // overwrite the first record and duplicate that first lesson on return.
    const inflightRecordKey = (lang, courseId, coordinates) =>
      `${authStore.userId}:${lang}:${courseId}:${coordinates}`;

    const persistInflightRecords = (records) => {
      try {
        if (Object.keys(records).length === 0) {
          localStorage.removeItem(STORAGE_KEYS.LESSON_INFLIGHT);
        } else {
          localStorage.setItem(
            STORAGE_KEYS.LESSON_INFLIGHT,
            JSON.stringify(records)
          );
        }
      } catch (error) {
        // Storage may be unavailable (Safari private mode, quota). Losing the
        // record costs a duplicated lesson at worst; throwing here would cost
        // the tap itself.
        console.warn("Failed to record the in-flight lesson start:", error);
      }
    };

    // Every read prunes, so an expired entry stops being a resume source AND
    // stops holding a tile in its spinner state.
    const readInflightRecords = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEYS.LESSON_INFLIGHT);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          return {};
        }
        const now = Date.now();
        const live = {};
        let prunedAny = false;
        for (const [key, entry] of Object.entries(parsed)) {
          if (
            entry &&
            typeof entry.dialogId === "string" &&
            typeof entry.createdAt === "number" &&
            now - entry.createdAt <= LESSON_INFLIGHT_TTL_MS
          ) {
            live[key] = { dialogId: entry.dialogId, createdAt: entry.createdAt };
          } else {
            prunedAny = true;
          }
        }
        if (prunedAny) persistInflightRecords(live);
        return live;
      } catch {
        // Unreadable storage, or junk a previous version wrote. Behaving as if
        // there were no record is exactly today's behavior.
        return {};
      }
    };

    // localStorage is not a reactive dependency of a Vue render, so the map is
    // mirrored here and every write path updates the mirror. `inflightNow`
    // ticks while any entry is live, which is what makes an expiry repaint the
    // tile on its own instead of waiting for an unrelated re-render.
    const inflightRecords = ref({});
    const inflightNow = ref(Date.now());
    let inflightTicker = null;

    const stopInflightTicker = () => {
      if (inflightTicker === null) return;
      clearInterval(inflightTicker);
      inflightTicker = null;
    };

    const syncInflightRecords = (records = readInflightRecords()) => {
      inflightRecords.value = records;
      inflightNow.value = Date.now();
      if (Object.keys(records).length === 0) {
        // Nothing left to expire, so nothing left to tick for.
        stopInflightTicker();
        return;
      }
      // Never start a timer for a view that has gone away. A tap whose verdict
      // or probe was still in flight when the learner navigated off finishes
      // its create afterwards, and that write must still persist the record —
      // but a ticker started there would outlive the component and keep it
      // alive until the map happened to empty.
      if (inflightTicker === null && isMounted) {
        inflightTicker = setInterval(() => syncInflightRecords(), 30_000);
      }
    };

    syncInflightRecords();
    onUnmounted(stopInflightTicker);

    // The coordinates, within the course currently on screen, whose start is
    // still in flight. The tile's in-progress class and the Voice Ledger card's
    // CTA both read this one list, so the two can never contradict each other.
    const inflightLessonIds = computed(() => {
      const lang = studentInfo.value?.selectedLanguage;
      const courseId = courseData.value?.metadata?.courseId;
      if (!lang || !courseId) return [];
      const prefix = inflightRecordKey(lang, courseId, "");
      const ids = [];
      for (const [key, entry] of Object.entries(inflightRecords.value)) {
        if (!key.startsWith(prefix)) continue;
        if (inflightNow.value - entry.createdAt > LESSON_INFLIGHT_TTL_MS) {
          continue;
        }
        ids.push(key.slice(prefix.length));
      }
      return ids;
    });

    const recordInflightStart = (lang, courseId, coordinates, newDialogId) => {
      const records = {
        ...readInflightRecords(),
        [inflightRecordKey(lang, courseId, coordinates)]: {
          dialogId: newDialogId,
          createdAt: Date.now(),
        },
      };
      persistInflightRecords(records);
      syncInflightRecords(records);
    };

    const clearInflightStart = (key) => {
      const records = readInflightRecords();
      if (!(key in records)) return;
      delete records[key];
      persistInflightRecords(records);
      syncInflightRecords(records);
    };

    // The first of the three ways a record clears: the lesson node's dialogId
    // lands, at which point the existing `lesson.dialogId` branch of
    // handleLessonClick owns the reopen and the record is redundant. (The other
    // two are a PERMISSION_DENIED probe and the TTL.) Deliberately NOT cleared
    // by resetDialog(), which is the learner tapping back while the server is
    // still generating: an immediate retap has to find the record.
    const clearInflightForStampedLessons = (course, lang, courseId) => {
      if (!lang || !courseId) return;
      const records = readInflightRecords();
      const prefix = inflightRecordKey(lang, courseId, "");
      let changedAny = false;
      for (const unit of normalizePlanUnits(course)) {
        for (const week of unit.weeks) {
          for (const planLesson of week.lessons) {
            if (!planLesson.dialogId) continue;
            const key = `${prefix}${lessonId(
              unit.number,
              week.number,
              planLesson.number
            )}`;
            if (key in records) {
              delete records[key];
              changedAny = true;
            }
          }
        }
      }
      if (changedAny) {
        persistInflightRecords(records);
        syncInflightRecords(records);
      }
    };

    // The read rule on dialog-meta is `auth.uid === data.child('userId').val()`
    // (database.rules.json:183), so a node the server deleted is REFUSED rather
    // than returned as null. PERMISSION_DENIED is therefore the client's only
    // signal that the dialog is gone — on a limit breach, an entitlement
    // breach, or any generation failure. Every other error (offline, timeout)
    // keeps the record and reopens anyway, which fails closed against
    // duplicates.
    //
    // The wording has to be matched loosely, because the SDK produces two
    // different strings for the same refusal and only one of them is the
    // underscored form. A rejected `get()` arrives as a bare
    // `new Error("Permission denied")` with NO `code` property, built from the
    // server's payload in `repoGetValue`; a revoked LISTEN or a refused
    // transaction goes through `errorForServerCode` instead and reads
    // "permission_denied at /path: Client doesn't have permission...". The
    // first form is the one this probe actually receives, confirmed against the
    // database emulator with the real rules file installed.
    const isPermissionDenied = (error) => {
      if (String(error?.code ?? "").toUpperCase() === "PERMISSION_DENIED") {
        return true;
      }
      return /permission[\s_-]?denied/i.test(
        String(error?.message ?? error ?? "")
      );
    };

    // Opening a lesson records a `?lesson=1` history entry so back / "My Course"
    // can return to the overview. Onboarding-stage opens deliberately do not.
    const openDialog = (id) => {
      dialogId.value = id;
      openMarker();
    };

    const resetDialog = () => {
      dialogId.value = null;
      stripMarker();
      watchLatestLesson();
      refreshPlanLimit();
    };

    // Initialize student info watching when component is mounted
    if (authStore.getUserId()) {
      watchStudentInfo();

      // Add a watcher for courseData to collapse units/weeks when it changes
      watch(
        () => courseData.value?.plan?.units,
        () => {
          if (structuredPlan.value.length > 0) {
            // Use setTimeout to ensure this runs after the next DOM update
            setTimeout(() => collapseAllExceptActive(), 0);
          }
        }
      );
    }

    const {
      isLoading: isAssessmentLoading,
      startAssessment: triggerAssessment,
    } = useAssessment();

    // `effectiveStage` already encodes that the background interview is
    // finished, whether that shows up as its own `completedAt` or as the
    // pointer having moved past it. That covers every state the old
    // `COLLECT_BACKGROUND.completedAt` test covered, plus the
    // pointer-advanced-without-a-stamp shape, which used to show no control at
    // all. It also excludes the state the old test wrongly accepted: a course
    // whose pointer is already on BUILD_PLAN while the assessment stage never
    // received a dialogId, which put a Start Assessment button on the
    // plan-building card.
    const showStartAssessmentButton = computed(() => {
      return (
        stagePresentation.value.effectiveStage === CourseStage.ASSESS_LEARNER &&
        !courseData.value?.metadata?.stages?.ASSESS_LEARNER?.dialogId
      );
    });

    // The REAL pointer, not the clamped effective stage: `effectiveStage` is
    // BUILD_PLAN for every all-done state, including PLAN_IN_PROGRESS with an
    // empty plan and a watched course that reaches COMPLETED, and none of those
    // is a plan being built.
    const showPlanCrafting = computed(
      () =>
        courseData.value?.metadata?.currentStage === CourseStage.BUILD_PLAN
    );

    // Whether the focus card carries an action row at all. When none of the
    // three holds the card is its title and description alone, which is the
    // defined fallback rather than an oversight.
    const showStageAction = computed(
      () =>
        showContinueSetupButton.value ||
        showStartAssessmentButton.value ||
        showPlanCrafting.value
    );

    const startAssessment = async () => {
      // `disabled` only lands on the next render, so a double tap in the same
      // task gets here twice and would POST two assessment starts, minting two
      // dialogs where the learner sees one. The same synchronous guard that
      // `reopenSetup` and `handleWizardChatInstead` use, for the same reason.
      // `isAssessmentLoading` is `useAssessment`'s own ref and it is set
      // synchronously at the top of that call, so it is true by the time a
      // second tap in the same task reads it.
      if (isAssessmentLoading.value) return;
      impactLight();
      try {
        const newDialogId = await triggerAssessment(
          studentInfo.value.selectedLanguage,
          courseData.value.metadata.courseId
        );
        // A resolved call with no id is a failure, not a success: the composable
        // returns `data.dialogId` after checking `response.ok`, so a 200 with an
        // empty body used to leave the learner tapping the screen's single most
        // prominent control and getting neither a chat nor an error. This is the
        // same shape as the placeholder-Cloud-Functions-URL bug, where a
        // relative fetch answered by the SPA's catch-all route read as success.
        if (!newDialogId) {
          throw new Error("Assessment start returned no dialogId");
        }
        dialogId.value = newDialogId;
      } catch (error) {
        console.error("Failed to start the assessment:", error);
        showToast(i18n.global.t("errors.course.assessmentStartFailed"), "error");
      }
    };

    // Update the isPlanInProgress computed property
    const isPlanInProgress = computed(() => {
      return (
        courseData.value?.metadata?.currentStage ===
          CourseStage.PLAN_IN_PROGRESS && structuredPlan.value.length > 0
      );
    });

    // Helper method to generate unique lesson IDs
    const lessonId = (unitNumber, weekNumber, lessonNumber) => {
      return `${unitNumber}-${weekNumber}-${lessonNumber}`;
    };

    // State for collapsed sections
    const collapsedUnits = ref(new Set());
    const collapsedWeeks = ref(new Map()); // Map of unit number to Set of collapsed week numbers

    // Function to collapse all units and weeks except for active ones
    const collapseAllExceptActive = () => {
      const latest = latestLesson.value;
      if (!latest) return;

      // First, collapse all units
      collapsedUnits.value.clear();
      for (const unit of structuredPlan.value || []) {
        if (unit.number !== latest.unitNumber) {
          collapsedUnits.value.add(unit.number);
        }
      }

      // Then, collapse all weeks except for the active one in the active unit
      collapsedWeeks.value.clear();
      for (const unit of structuredPlan.value || []) {
        const weekSet = new Set();
        for (const week of unit.weeks || []) {
          // Collapse all weeks except for the active one in the active unit
          if (
            unit.number === latest.unitNumber &&
            week.number === latest.weekNumber
          ) {
            continue; // Skip active week
          }
          weekSet.add(week.number);
        }
        if (weekSet.size > 0) {
          collapsedWeeks.value.set(unit.number, weekSet);
        }
      }
    };

    // Toggle functions
    const toggleUnit = (unitNumber) => {
      if (collapsedUnits.value.has(unitNumber)) {
        collapsedUnits.value.delete(unitNumber);
      } else {
        collapsedUnits.value.add(unitNumber);
      }
    };

    const toggleWeek = (unitNumber, weekNumber) => {
      if (!collapsedWeeks.value.has(unitNumber)) {
        collapsedWeeks.value.set(unitNumber, new Set());
      }
      const weekSet = collapsedWeeks.value.get(unitNumber);
      if (weekSet.has(weekNumber)) {
        weekSet.delete(weekNumber);
      } else {
        weekSet.add(weekNumber);
      }
    };

    // Check functions
    const isUnitCollapsed = (unitNumber) => {
      return collapsedUnits.value.has(unitNumber);
    };

    const isWeekCollapsed = (unitNumber, weekNumber) => {
      return collapsedWeeks.value.get(unitNumber)?.has(weekNumber) ?? false;
    };

    const isLessonCompleted = (lesson) => {
      return !!lesson.completedAt;
    };

    // Coordinates are optional so callers that only have the lesson node keep
    // working; they are supplied from the tree, where the unit and week are in
    // scope, and are what lets an in-flight start show as in progress.
    const isLessonInProgress = (lesson, unitNumber, weekNumber) => {
      if (lesson.completedAt) return false;
      if (lesson.startedAt) return true;
      // The dialog-meta write landed and the server has not stamped the lesson
      // node yet. A tile still reading "never started" would be a lie the
      // learner answers with the second tap this record exists to prevent.
      if (unitNumber == null || weekNumber == null) return false;
      return inflightLessonIds.value.includes(
        lessonId(unitNumber, weekNumber, lesson.number)
      );
    };

    const isLessonLocked = (unitNumber, weekNumber, lessonNumber) => {
      // Latest lesson = either in progress or the next available
      if (!latestLesson.value) return false;

      // If this lesson comes after the latest available lesson, it should be locked
      return isLessonAfter(unitNumber, weekNumber, lessonNumber, latestLesson.value);
    };

    // Computed (not a function) on purpose: the template calls isLessonLocked
    // several times per lesson card, and each call used to re-scan the whole
    // plan — O(N^2) per render. The cache invalidates with structuredPlan.
    const latestLesson = computed(() => findLatestLesson(structuredPlan.value));

    const isLessonAfter = (unitNum, weekNum, lessonNum, latestLesson) => {
      if (unitNum > latestLesson.unitNumber) return true;
      if (unitNum < latestLesson.unitNumber) return false;

      if (weekNum > latestLesson.weekNumber) return true;
      if (weekNum < latestLesson.weekNumber) return false;

      return lessonNum > latestLesson.lessonNumber;
    };

    // Single-flighted: creating a dialog is an awaited write, and nothing
    // about a lesson tile or the ledger CTA stops a second tap landing during
    // it. Two taps used to mean two dialog-meta records - two openers
    // generated, two lessons counted against the plan, one dialog abandoned.
    const handleLessonClick = singleFlight(async (lesson, unitNumber, weekNumber) => {
      if (isLessonLocked(unitNumber, weekNumber, lesson.number)) {
        showToast(i18n.global.t("course.list.lockedTooltip"), "info");
        return;
      }

      impactLight();

      const clickedLesson = lessonId(unitNumber, weekNumber, lesson.number);
      const latest = latestLesson.value;

      // Make sure the unit and week of this lesson are expanded
      if (collapsedUnits.value.has(unitNumber)) {
        collapsedUnits.value.delete(unitNumber);
      }

      if (collapsedWeeks.value.get(unitNumber)?.has(weekNumber)) {
        const weekSet = collapsedWeeks.value.get(unitNumber);
        if (weekSet) {
          weekSet.delete(weekNumber);
        }
      }

      if (lesson.completedAt && lesson.dialogId) {
        openDialog(lesson.dialogId);
        selectLesson({ ...lesson, id: clickedLesson });
        return;
      }

      if (
        latest &&
        clickedLesson ===
          lessonId(latest.unitNumber, latest.weekNumber, latest.lessonNumber)
      ) {
        // We are clicking the latest available lesson.
        // A dialogId is checked before startedAt: a lesson can carry one
        // without the other, and creating a second dialog for it would strand
        // the conversation the learner already had.
        if (lesson.dialogId) {
          openDialog(lesson.dialogId);
        } else if (lesson.startedAt) {
          console.error(
            "Lesson already started but no dialogId exists for lesson",
            clickedLesson
          );
          showToast(i18n.global.t("errors.lesson.missingDialog"), "error");
          return;
        } else {
          // Fail-closed creation gate. The wall's totality used to be the only
          // thing preventing this write at the ceiling; now that the tree
          // survives the wall, the check has to be explicit. It also closes a
          // live pre-existing bypass: the Voice Ledger CTA resolves `startLesson`
          // for a never-started lesson and hands it straight to this handler,
          // so a flagged free user at the ceiling could already mint a dialog
          // the server refuses and deletes (the hung-lesson failure).
          //
          // Everything the write is ABOUT, captured before the await. This is
          // the first await this handler has ever had between the tap and the
          // dialog-meta write, so it opens a window nothing used to be able to
          // land in — and a language switch lands in it: the newer resolution
          // publishes a verdict for language B, the parked tap reads it as
          // permission, and the write files language A's lesson coordinates
          // under language B's course. The tap belongs to the tree that was on
          // screen when it happened; if that tree is gone, so is the tap.
          const tapLang = studentInfo.value.selectedLanguage;
          const tapCourseId = courseData.value?.metadata?.courseId;
          const verdict = await resolveCreationVerdict();
          if (
            studentInfo.value?.selectedLanguage !== tapLang ||
            courseData.value?.metadata?.courseId !== tapCourseId
          ) {
            // Silently: the learner is looking at a different course now, and
            // neither an upsell nor an error is a true thing to say about the
            // tap they made on the previous one.
            return;
          }
          if (verdict === "atLimit") {
            // Non-destructive: the sheet goes OVER the tree, so dismissing it
            // returns the learner to the lesson they still have open.
            showLimitUpsell.value = true;
            return;
          }
          // The in-flight restore sits BETWEEN the two refusing verdicts on
          // purpose. It is behind "atLimit" for the reason FreeChatView gives
          // (FreeChatView.vue:576-579): at the ceiling a dialog the server
          // refused and deleted is not a resume source, and restoring one would
          // drop the learner into a ChatScreen for a dialog that no longer
          // exists. It is AHEAD of "unknown" because reopening spends no
          // allowance, so a learner whose usage read failed should still get
          // back to the dialog already being generated for them rather than be
          // locked out until the record expires.
          const inflightKey = inflightRecordKey(
            tapLang,
            tapCourseId,
            clickedLesson
          );
          const inflight = readInflightRecords()[inflightKey];
          if (inflight) {
            let dialogWasDeleted = false;
            try {
              // ONE probe, not a poll: the question is only whether the dialog
              // this record points at still exists.
              const snapshot = await get(
                dbRef(getDatabase(), `dialog-meta/${inflight.dialogId}`)
              );
              dialogWasDeleted = !snapshot.exists();
            } catch (error) {
              dialogWasDeleted = isPermissionDenied(error);
              if (!dialogWasDeleted) {
                console.warn(
                  "In-flight lesson probe failed; reopening anyway",
                  error
                );
              }
            }
            // Clearing is safe either way: it is a statement about the
            // coordinates that were tapped, which a later language switch does
            // not change.
            if (dialogWasDeleted) clearInflightStart(inflightKey);
            // The probe is an await, so the same supersession checks the
            // verdict needed apply again, and they apply to BOTH outcomes. A
            // reopen after a switch would put language A's dialog on top of
            // language B's course; a CREATE after a switch is worse still,
            // because the write below reads the LIVE language and course and
            // would file language A's lesson coordinates under language B,
            // charging a lesson nobody asked for.
            if (
              !isMounted ||
              studentInfo.value?.selectedLanguage !== tapLang ||
              courseData.value?.metadata?.courseId !== tapCourseId
            ) {
              return;
            }
            if (!dialogWasDeleted) {
              openDialog(inflight.dialogId);
              selectLesson({ ...lesson, id: clickedLesson });
              return;
            }
          }

          if (verdict === "unknown") {
            showToast(i18n.global.t("errors.lesson.startFailed"), "error");
            return;
          }
          // Lesson not started: add a new entry to dialog-meta and assign dialogId
          const createKey = `${authStore.userId}/${studentInfo.value.selectedLanguage}/${clickedLesson}`;
          if (lessonCreatesInFlight.has(createKey)) return;
          lessonCreatesInFlight.add(createKey);
          try {
            const db = getDatabase();
            const dialogMetaRef = push(dbRef(db, "dialog-meta"));
            // Record the start BEFORE the write, for FreeChatView's reason
            // (FreeChatView.vue:640-643): if anything interrupts the page
            // between the two, the record TTLs out harmlessly, whereas
            // recording afterwards would let a crash mid-write leave a
            // dialog-meta row the learner could never find again.
            recordInflightStart(
              tapLang,
              tapCourseId,
              clickedLesson,
              dialogMetaRef.key
            );
            // The CAPTURED language and course, not the live refs. Nothing
            // awaits between the supersession check above and this write, so
            // the two are equal today; writing the captured pair is what keeps
            // them equal if a future await is ever added here, and it is the
            // same pair the record was just filed under.
            await set(dialogMetaRef, {
              userId: authStore.userId,
              lang: tapLang,
              type: "LESSON",
              courseId: tapCourseId,
              unitNumber: unitNumber,
              weekNumber: weekNumber,
              lessonNumber: lesson.number,
              // Tap time (seconds): true start of the tap-to-opener wait, before
              // the backend generates the lesson opener. tutor-openerWait anchors
              // on this. See monitoring.recordPerceivedLatencyMetrics.
              clientStartedAt: Math.floor(Date.now() / 1000),
            });
            // The write landed, so the record is now true whatever happens
            // next; opening the chat is the only part that needs a live view.
            // openDialog pushes a `?lesson=1` history entry onto whatever route
            // is current, so after a navigation away it would mark an unrelated
            // page. (That exposure predates this unit: the verdict has been an
            // await on this path since the fail-closed creation gate shipped.)
            if (!isMounted) return;
            openDialog(dialogMetaRef.key);
          } catch (error) {
            console.error("Failed to create dialog entry for lesson", error);
            // A rejected set() is an explicit server refusal, not a dropped
            // connection: RTDB queues a write it cannot deliver and settles it
            // on reconnect. So the dialog this record names does not exist, and
            // leaving the record would spin the tile and label the ledger
            // "continue" for five minutes beside a toast saying the start
            // failed.
            clearInflightStart(
              inflightRecordKey(tapLang, tapCourseId, clickedLesson)
            );
            showToast(i18n.global.t("errors.lesson.startFailed"), "error");
            return;
          } finally {
            lessonCreatesInFlight.delete(createKey);
          }
        }
      }

      // Continue by selecting the lesson
      selectLesson({ ...lesson, id: clickedLesson });
    });

    const handleUpgradeRequest = async (planType) => {
      isProcessingUpgrade.value = true;
      const [planName, billingCycleName] = planType.split("-");
      const plan = pricingStore.plans[planName];
      const lookupKey =
        billingCycleName === "month"
          ? plan.monthlyLookupKey
          : plan.yearlyLookupKey;

      try {
        await stripeUtils.startCheckout(lookupKey);
      } catch (error) {
        console.error("Error creating upgrade checkout session:", error);
        showToast(i18n.global.t("errors.billing.upgradeStartFailed"), "error");
      } finally {
        isProcessingUpgrade.value = false;
      }
    };

    return {
      selectedLesson,
      filterStatus,
      filteredLessons,
      selectLesson,
      completeLesson,
      startJourney,
      dialogId,
      wizardOpen,
      wizardDialogId,
      wizardCreateState,
      wizardCreateRetryable,
      retryCourseCreate,
      showContinueSetupButton,
      continueTapPending,
      reopenSetup,
      handleWizardCompleted,
      handleWizardClosed,
      handleWizardChatInstead,
      resetDialog,
      studentInfo,
      courseData,
      stagePresentation,
      stageSegments,
      setupStepTotal: SETUP_STAGES.length,
      stageHeadlineKey,
      stageIntroKey,
      stageTitleKey,
      stageDescriptionKey,
      stageIcon,
      stageStatusKey,
      isLoading,
      showStartAssessmentButton,
      showPlanCrafting,
      showStageAction,
      isAssessmentLoading,
      startAssessment,
      isPlanInProgress,
      structuredPlan,
      lessonId,
      toggleUnit,
      toggleWeek,
      isUnitCollapsed,
      isWeekCollapsed,
      isLessonCompleted,
      isLessonInProgress,
      inflightLessonIds,
      isLessonLocked,
      handleLessonClick,
      featureFlags,
      hasReachedLimit,
      limitResolved,
      showLimitWall,
      showLimitUpsell,
      plansSheetOpen,
      openPlansSheet,
      limitBlockProps,
      limitUpsellLabel,
      resumableLesson,
      effectiveLessonLimit,
      lifetimeUsage,
      showSampleComplete,
      hasFreeChatLeft,
      goToFreeChat,
      isProcessingUpgrade,
      pricingStore,
      handleUpgradeRequest,
      handleIapPurchaseComplete,
      isFreePlan,
      currentPlanId,
      userCountry,
      currentProvider,
    };
  },
};
</script>

<style scoped>
/* Base Layout */
.lessons-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1 1 auto;
  padding: 1.5rem;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  background: var(--al-surface-page);
  min-height: 100vh;
  min-height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Welcome Container.

   This is a ROW flexbox (no flex-direction), so its VERTICAL clipper is
   align-items, not justify-content -- justify-content: center here is
   horizontal and harmless. `align-items: center` on a scrolling box clips the
   top of overflowing content with no way to scroll back to it (the same
   failure Modal.vue documents). `flex-start` rather than simply dropping the
   declaration, because the default resolves to `stretch`, which is a different
   layout again. The vertical centering the learner still sees comes from
   .welcome-header's auto block margins below. */
/* Not a scroller, and no height of its own.

   `.lessons-view` above is already the scroller for this view: `overflow-y:
   auto` plus a minimum height that fills the shell (the base `100dvh` here on
   desktop, `100%` of `.content-area`'s content box on mobile, where the
   media blocks at the bottom of this file override it). A second `overflow-y`
   here made the two nest: the outer box scrolled the free-sample card off the
   top while this box held its content still, so the card vanished against a
   stationary background and both scrollbars showed at once.

   The old `min-height: calc(100dvh - 200px)` was a hardcoded guess at the
   height of everything above this container. The cards above are SIBLINGS, so
   the guess has to be re-tuned every time one is added or resized -- and it
   was already wrong for two: card (~91px) plus Voice Ledger card (~192px)
   overshoots the 200px reserve, forcing this box past the room left for it and
   putting the outer box into scroll. `flex: 1 1 auto` asks the flex algorithm
   for exactly the leftover space instead, which needs no constant and stays
   right for any number of siblings. `height: 100%` went with it: it would
   re-impose the full parent height that the reserve existed to walk back. */
.welcome-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1 1 auto;
}

/* Welcome Header.

   `margin: auto`, not `margin: 0 auto`: the auto BLOCK margins are what centre
   this in the space the card leaves, and they collapse to zero when the header
   is taller than the container, so an overflowing hero stays scrollable from
   its top. The mobile override below already shipped this pattern; this is the
   desktop half of it. Only the no-course hero uses this block now: the
   course-exists body is `.course-setup`, which is top-aligned. */
.welcome-header {
  text-align: center;
  max-width: 100%;
  margin: auto;
  padding: 2rem 1rem;
  flex: 0 0 auto;
}

.empty-state-icon {
  font-size: 48px;
  color: var(--al-primary);
  background: var(--al-primary-soft);
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

/* On the H1-mobile step of DESIGN.md's scale (30px / 1.12), in the text role
   rather than in --al-primary: blue is the action colour here, and a 40px
   800-weight blue heading was both off the scale and competing with the CTA
   below it. 44px from 1024px is the H1-desktop step. */
.welcome-header h1 {
  font-size: 1.875rem;
  line-height: 1.12;
  color: var(--al-text);
  margin-bottom: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.welcome-description {
  font-size: 1rem;
  line-height: 1.55;
  color: var(--al-text-muted);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 400;
}

/* Start Journey Button */
.start-journey-btn {
  padding: 1rem 2.5rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--al-white);
  /* --al-primary-hover, not --al-primary: white text on --al-primary is only
     about 3.3:1, which fails AA. FreeChatView.vue's solid blue button moved for
     the same reason. The hover and press states therefore move down to
     --al-ink-600, DESIGN.md's pressed hue, because a hover onto the hue the
     button already wears would be a no-op. */
  background: var(--al-primary-hover);
  border: none;
  border-radius: var(--al-radius-lg);
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    box-shadow var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  box-shadow: var(--al-shadow-md);
  width: 100%;
  max-width: 320px;
  min-height: 48px;
  margin: 0 auto;
  position: relative;
}

/* Hover only where hover is real: on touch WebViews :hover latches onto the
   last-tapped element until the next tap elsewhere, leaving these CTAs painted
   as hovered after the finger is gone. The :active press feedback below stays
   unguarded — on touch it is the only tap affordance. */
@media (hover: hover) and (pointer: fine) {
  .start-journey-btn:hover {
    background: var(--al-ink-600);
  }
}

.start-journey-btn:active {
  background: var(--al-ink-600);
  transform: scale(0.98);
}

/* The base hue, so the disabled button is not LIGHTER than the enabled one —
   which is what --al-primary would now be. */
.start-journey-btn:disabled {
  background-color: var(--al-primary-hover);
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.start-journey-btn:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

/* ---- Course setup screen (the pre-plan body once a course exists) -------- */

/* A plain block child of .welcome-container rather than three siblings.
   .welcome-container is a ROW flexbox whose `align-items: flex-start` is the
   guard against the documented top-clipping bug, and whose declarations are
   pinned by tests; adding `flex-direction: column` there would silently turn
   that declaration into a shrink-wrap rule. Horizontal centring comes from the
   parent's `justify-content: center`, and `margin-inline: auto` covers the case
   where this block is not a flex child. Top-aligned on purpose: once the course
   exists this body presents a task, so it should not float down the viewport. */
.course-setup {
  width: 100%;
  max-width: 640px;
  min-width: 0;
  margin-inline: auto;
  text-align: left;
}

/* The stage panel and the focus card now live in
   src/components/StagePanel.vue, which My Course and Today both render. The
   rules below are the ones that are still only My Course's: the crafting
   sentence inside the focus card, the setup list under it, and the two setup
   CTAs. */
/* The plan build has no button; it has a sentence that a screen reader hears
   when the stage advances underneath it. Not italic — the old .progress-text
   was, and italic body copy reads as an aside rather than as status. */
.stage-crafting {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  min-height: 44px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--al-text-muted);
}

.crafting-spinner {
  color: var(--al-orange);
  font-size: 18px;
}

/* The setup list: the overview the old three-card checklist gave, quiet enough
   that it does not compete with the focus card above it. */
.setup-list {
  margin-top: var(--al-space-lg);
}

.setup-list-label {
  margin: 0 0 4px;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--al-text-muted);
}

.setup-steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.setup-step {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 8px 0;
  border-bottom: 1px solid var(--al-border);
}

.setup-step:last-child {
  border-bottom: none;
}

.step-disc {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid var(--al-border);
  color: var(--al-text-muted);
}

/* --al-success-dark rather than --al-success: white on #059669 is about 3.6:1,
   which clears the 3:1 floor for a non-text mark, while white on --al-success
   is about 2.6:1 and does not. */
.setup-step.done .step-disc {
  background: var(--al-success-dark);
  border-color: var(--al-success-dark);
  color: var(--al-white);
}

.setup-step.current .step-disc {
  border: 2px solid var(--al-orange);
  background: var(--al-surface-raised);
  color: var(--al-text);
}

.step-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  line-height: 1.4;
  color: var(--al-text);
}

.setup-step.current .step-name {
  font-weight: 600;
}

.setup-step.upcoming .step-name {
  color: var(--al-text-muted);
}

/* --al-text-muted for all three states: --al-success-dark at 13px is about
   3.8:1 on white, below the AA bar for text this size. The disc carries the
   colour, the word carries the meaning. */
.step-status {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--al-text-muted);
}

/* --al-success-dark remaps to #34d399 in dark, where an ink check reads and a
   white one does not. */
:root[data-theme="dark"] .setup-step.done .step-disc {
  color: var(--al-ink-900);
}

/* The two setup CTAs. Both fill the focus card's action row and stand 52px
   tall (48px below 400px wide), which clears DESIGN.md's 48px preferred touch
   target with room to spare. */
/* --al-primary-hover rather than --al-primary, for the same AA reason as
   .start-journey-btn above: white on --al-primary is only about 3.3:1. */
.button.setup-continue {
  background: var(--al-primary-hover);
  color: var(--al-white);
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: var(--al-radius-md);
  font-size: 1rem;
  font-weight: 600;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  width: 100%;
  min-height: 52px;
  cursor: pointer;
}

/* The pending state must not depend on source order: the prod CSS minifier
   reorders equal-specificity duplicate selectors (see #573), and bare :hover /
   :active / :disabled here would all be (0,3,0). Excluding :disabled from the
   interactive rules makes them mutually exclusive instead of a tie. */
@media (hover: hover) and (pointer: fine) {
  .button.setup-continue:hover:not(:disabled) {
    background: var(--al-ink-600);
  }
}

.button.setup-continue:active:not(:disabled) {
  background: var(--al-ink-600);
  transform: scale(0.98);
}

/* Exactly one :disabled rule for this button, here and nowhere else — no
   responsive override may add a second. It keeps the enabled hue so the dim is
   the only difference. */
.button.setup-continue:disabled {
  background: var(--al-primary-hover);
  opacity: 0.65;
  /* Not `not-allowed`: the tap WAS accepted and is in flight. */
  cursor: default;
}

.button.setup-continue:focus-visible,
.button.assessment:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

/* AA: white text on --al-orange is ~2.6:1 (fails). Ink-900 on --al-orange
   is ~6.7:1, so the label uses ink instead of white. */
.button.assessment {
  background: var(--al-orange);
  color: var(--al-ink-900);
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: var(--al-radius-md);
  font-size: 1rem;
  font-weight: 600;
  transition: box-shadow var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  width: 100%;
  box-shadow: var(--al-shadow-sm);
  position: relative;
  min-height: 52px;
  cursor: pointer;
}

/* :not(:disabled) for the same reason as .setup-continue above: bare :hover,
   :active and :disabled here would all be (0,3,0), and the production minifier
   reorders equal-specificity duplicates, so a disabled button could win a
   hover shadow and a press scale while its request is still in flight. */
@media (hover: hover) and (pointer: fine) {
  .button.assessment:hover:not(:disabled) {
    box-shadow: var(--al-shadow-md);
  }
}

.button.assessment:active:not(:disabled) {
  transform: scale(0.98);
}

.button.assessment:disabled {
  background: var(--al-orange);
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Lessons List */
.lessons-list {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--al-surface-raised);
  border-radius: var(--al-radius-lg);
  padding: 2rem;
  box-shadow: var(--al-shadow-md);
  border: 1px solid var(--al-border);
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.list-header h2 {
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--al-primary);
  margin-bottom: 2rem;
  text-align: center;
}

.units {
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-right: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: var(--al-border) transparent;
  width: 100%;
}

.units::-webkit-scrollbar {
  width: 6px;
}

.units::-webkit-scrollbar-track {
  background: transparent;
}

.units::-webkit-scrollbar-thumb {
  background-color: var(--al-border);
  border-radius: 6px;
}

/* Unit Styles */
.unit {
  background: var(--al-surface-raised);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: min-content;
  flex: 0 0 auto;
  transition: border-color var(--al-dur-short) var(--al-ease-move),
    box-shadow var(--al-dur-short) var(--al-ease-move);
  margin-bottom: 1.5rem;
  border: 1px solid var(--al-border);
  position: relative;
}

.unit:hover {
  box-shadow: var(--al-shadow-md);
}

.unit.collapsed {
  flex: 0 0 auto;
}

.unit-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--al-surface-raised);
  padding: 1.5rem;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  border-radius: 6px;
  min-height: 60px;
}

.unit-header:hover {
  background: var(--al-surface-sunken);
}

.unit-header:active {
  transform: scale(0.98);
}

.unit-header-content {
  flex: 1;
  margin-right: 1rem;
}

.unit-number {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--al-text);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.unit-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--al-text);
  margin-bottom: 0.5rem;
}

.unit-goal {
  color: var(--al-text-muted);
  font-size: 0.875rem;
  line-height: 1.5;
  margin-top: 0.5rem;
  font-style: italic;
}

/* Week Styles */
.weeks {
  padding: 0 1rem 1rem 1rem;
  flex: 1;
  width: 100%;
}

.week {
  background: var(--al-surface-sunken);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  overflow: hidden;
  width: 100%;
}

.week:last-child {
  margin-bottom: 0;
}

.week-header {
  padding: 1rem;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-radius: 6px;
  min-height: 56px;
}

.week-header:hover {
  background: var(--al-surface-sunken);
}

.week-header:active {
  transform: scale(0.98);
}

.week-header-content {
  flex: 1;
}

.week-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--al-text);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.week-title {
  font-size: 1rem;
  font-weight: 500;
  color: var(--al-text);
}

/* Lesson Grid and Cards */
.lessons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.lesson-card {
  display: flex;
  flex-direction: column;
  background: var(--al-surface-raised);
  border-radius: var(--al-radius-lg);
  padding: 1.5rem 1rem;
  cursor: pointer;
  transition: border-color var(--al-dur-short) var(--al-ease-move),
    box-shadow var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  border: 2px solid var(--al-border);
  position: relative;
  min-height: 120px;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-shadow: var(--al-shadow-sm);
  overflow: hidden;
  min-width: 140px;
}

.lesson-card.completed {
  border-color: var(--al-success);
  background: color-mix(in srgb, var(--al-success) 10%, var(--al-surface-raised));
  box-shadow: var(--al-shadow-sm);
}

.lesson-card.completed .lesson-status-icon {
  color: var(--al-success);
}

/* Dashed border + muted palette reads as "locked" without dimming text
   below AA contrast (the old opacity: 0.7 did). */
.lesson-card.locked {
  cursor: not-allowed;
  background: var(--al-surface-sunken);
  border-style: dashed;
  border-color: var(--al-border);
  box-shadow: none;
}

.lesson-card.locked .lesson-status-icon,
.lesson-card.locked .lesson-number,
.lesson-card.locked h3 {
  color: var(--al-text-muted);
}

/* Leave room for the unlock hint below the title. */
.lesson-card.locked h3 {
  -webkit-line-clamp: 2;
}

.locked-hint {
  font-size: 0.7rem;
  color: var(--al-text-muted);
  line-height: 1.35;
  margin-top: 0.4rem;
}

.lesson-card.in-progress {
  border-color: var(--al-primary);
  background: var(--al-primary-soft);
  box-shadow: var(--al-shadow-sm);
}

/* WCAG 1.4.11: --al-primary on --al-primary-soft is ~2.7:1, below the 3:1
   non-text minimum for a meaningful status icon; primary-hover is ~3.8:1. */
.lesson-card.in-progress .lesson-status-icon {
  color: var(--al-primary-hover);
}


.lesson-status-icon {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  font-size: 1.25rem;
}

.lesson-card:not(.locked):hover {
  box-shadow: var(--al-shadow-md);
}

.lesson-card:not(.locked):active {
  transform: scale(0.98);
}

.lesson-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--al-text);
}

.lesson-card h3 {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--al-text);
  line-height: 1.3;
  margin-top: 0.5rem;
  margin-bottom: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Chat Components */
.lesson-chat {
  flex: 1 1 auto;
  min-height: 0;
  background: var(--al-surface-raised);
  border-radius: 8px;
  box-shadow: var(--al-shadow-sm);
  padding-top: 0;
  margin-top: 0;
  overflow: visible;
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.fullscreen-chat {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  background-color: var(--al-surface);
  padding: 0.5rem;
  margin: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chat-screen-container {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

/* Responsive Design */
@media (min-width: 768px) {
  .lessons-view {
    padding: 2rem;
    height: 100%;
    width: 100%;
  }

  .welcome-header {
    max-width: 900px;
    width: 100%;
    padding: 2rem 1rem;
  }

  .welcome-container .welcome-description {
    margin-bottom: 2rem;
  }

  .start-journey-btn {
    width: auto;
    padding: 1rem 2.5rem;
    font-size: 1.25rem;
  }

  .lessons-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }

  .unit-header {
    padding: 1.5rem;
  }

  .unit-title {
    font-size: 1.25rem;
  }

  .week-title {
    font-size: 1rem;
  }

  .fullscreen-chat {
    padding: 1rem;
  }
}

@media (min-width: 1024px) {
  .lessons-view {
    padding: 3rem;
    width: 100%;
  }

  /* Scoped through .welcome-container so it outranks the base
     `.welcome-header h1` rule by specificity rather than by source order; at
     equal specificity the production minifier decides the winner, which has
     broken release builds in this repo before (#573). The two mobile overrides
     below are scoped for the same reason. */
  .welcome-container .welcome-header h1 {
    font-size: 2.75rem;
    line-height: 1.08;
  }

  .lessons-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }
}

/* The shell's mobile range runs through 768px INCLUSIVE
   (DashboardView.vue's `@media (max-width: 768px)`), while every mobile block
   in this file stops at 767px. That one-pixel band is why this rule exists
   separately from the 767px block below: without it, a 768px-wide window keeps
   the base `min-height: 100dvh` while the shell is still in mobile mode, and
   the whole point of the override is lost exactly there.

   `.lessons-view.lessons-view`, not a bare `.lessons-view`: the base rule this
   has to beat has the same one-class specificity, so at equal specificity the
   winner is decided by the order the production CSS minifier happens to emit
   the two chunks in — which has silently broken release builds in this repo
   before (see the same note on DashboardView's transition rules). Repeating
   the class buys the specificity without reaching outside this component.

   Why 100% rather than any `100dvh - Npx` constant: `.content-area` is a flex
   column with a definite height, so 100% here IS its content box, top
   safe-area inset and tab-bar reserve already subtracted. Every constant is a
   guess at those two numbers, and guessing high hands `.content-area` scroll
   slack it should not have — scroll into that slack and a tab switch clamps
   scrollTop back to zero the instant the view leaves normal flow. */
@media (max-width: 768px) {
  .lessons-view.lessons-view {
    min-height: 100%;
  }
}

@media (max-width: 767px) {
  .lessons-view {
    /* Where `min-height: calc(100dvh - 56px)` used to be, kept as an explicit
       declaration rather than deleted. It is redundant TODAY — the two-class
       768px rule above outranks it and already covers every width this block
       does — but it is the declaration that survives if that rule is ever
       narrowed or dropped. Delete it instead and the fallback below 767px
       becomes the base `min-height: 100dvh`, which overshoots the content area
       by MORE than the pair removed here did. */
    min-height: 100%;
    margin-top: 0;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
  }

  .welcome-container {
    /* The retuned `100dvh - 170px` floor that used to live here is gone for the
       same reason as the 200px base one: it was a per-card constant on a box
       whose siblings vary, and it had to be re-derived every time the stack
       above changed. `flex: 1 1 auto` from the base rule fills the space the
       shell actually leaves, tab bar and cards already subtracted. */
    padding: 1rem;
    justify-content: center;
  }

  /* The auto block margins here are the pattern the base rule now shares; the
     explicit margin-top/bottom stay so this override does not have to know
     what the base rule's `margin: auto` shorthand also sets. */
  .welcome-header {
    padding: 1rem 0.75rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: auto;
    margin-bottom: auto;
  }

  .welcome-container .welcome-header h1 {
    margin-bottom: 0.75rem;
    text-align: center;
    padding: 0 0.5rem;
  }

  .welcome-container .welcome-description {
    margin-bottom: 1.25rem;
    text-align: center;
    max-width: 100%;
    padding: 0 0.75rem;
  }

  .start-journey-btn {
    padding: 0.875rem 1.75rem;
    font-size: 1rem;
    max-width: 260px;
    width: 100%;
    min-height: 48px;
  }

  .lessons-list {
    padding: 0.75rem;
  }

  .list-header h2 {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }

  .unit-header {
    padding: 1rem;
    min-height: 56px;
  }

  .unit-title {
    font-size: 1rem;
  }

  .unit-goal {
    font-size: 0.8rem;
  }

  .lessons-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 0.75rem;
    padding: 0.875rem;
  }

  .lesson-card {
    padding: 1rem 0.875rem;
    min-height: 120px;
    min-width: 120px;
  }

  .lesson-card h3 {
    font-size: 0.85rem;
    line-height: 1.4;
    -webkit-line-clamp: 3;
  }

  /* Re-targeted from the retired `.checklist-item .button.assessment`. Scoped
     through `.focus-action` rather than written as a bare `.button.assessment`
     so it does not tie with the base rule at (0,2,0): the production CSS
     minifier reorders equal-specificity duplicate selectors, which has silently
     broken release builds in this repo before (#573). */
  .focus-action .button.assessment {
    padding: 0.875rem 1.25rem;
    font-size: 0.95rem;
  }

  .week-header {
    padding: 0.875rem;
    min-height: 52px;
  }

  .week-title {
    font-size: 0.9rem;
  }

  .week-number {
    font-size: 0.7rem;
  }

  .lesson-status-icon {
    top: 0.5rem;
    right: 0.5rem;
    font-size: 1.1rem;
  }

  .lesson-number {
    font-size: 0.7rem;
  }

  .fullscreen-chat {
    padding: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 49;
  }

  /* Mobile-specific touch improvements */
  .unit-header,
  .week-header,
  .lesson-card {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }

  .units {
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }

  .lessons-grid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.875rem;
    padding: 0.875rem;
  }

  .unit {
    margin-bottom: 1rem;
  }

  .week {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 480px) {
  .lessons-view {
    padding: 0.5rem;
  }

  .welcome-header {
    padding: 0.75rem 0.5rem;
  }

  .welcome-container .welcome-header h1 {
    padding: 0 0.25rem;
  }

  .welcome-container .welcome-description {
    padding: 0 0.5rem;
  }

  .lessons-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.625rem;
    padding: 0.75rem;
  }

  .lesson-card {
    padding: 0.875rem 0.75rem;
    min-height: 110px;
    min-width: 110px;
  }

  .lesson-card h3 {
    font-size: 0.8rem;
    line-height: 1.4;
    -webkit-line-clamp: 3;
  }

  .unit-header,
  .week-header {
    min-height: 48px;
  }

  .start-journey-btn {
    max-width: 240px;
    min-height: 44px;
  }
}

@media (min-width: 481px) and (max-width: 767px) {
  .lessons-view {
    padding: 1rem;
  }

  .welcome-header {
    padding: 1.25rem 1rem;
  }

  .lessons-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

/* The narrowest phones (an iPhone SE in landscape-free portrait is 320px). The
   CTAs drop to DESIGN.md's 48px preferred touch target. Written through
   `.course-setup .focus-action` so the CTA floor outranks both base button
   rules instead of tying with them; a second `:disabled` rule is deliberately
   NOT added here, because `.button.setup-continue` must carry exactly one. The
   panel's own side padding at this width moved to StagePanel.vue with the
   panel. */
@media (max-width: 399px) {
  .course-setup .focus-action .button {
    min-height: 48px;
  }
}
</style>
